/**
 * 🎯 Firestore 기반 이벤트 트래킹 시스템
 * 
 * GA4와 별개로 자체 Firestore에 이벤트를 저장하여
 * 팩트 기반의 정확한 분석 데이터를 수집합니다.
 * 
 * 주요 추적 이벤트:
 * - 결제 퍼널: PRO 모달 → 결제 페이지 → 결제 완료
 * - 회원가입 퍼널: 방문 → 가입 → 첫 활동
 * - 기능 사용: 각 기능별 사용 빈도
 * 
 * Author: feature/yeongu/analytics-dashboard
 * Created: 2026-01-28
 */

import { auth } from '../firebase/config';
import { getApiBaseUrl } from './apiConfig';
import { getAnonymousUserId } from './anonymousUser';
import { getMetaMatchParams } from './analytics';

// ============================================================================
// 세션 관리
// ============================================================================

// 세션 ID 생성 (브라우저 탭별 고유)
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
    sessionStorage.setItem('analytics_session_start', Date.now().toString());
  }
  return sessionId;
};

// 세션 시작 시간
const getSessionStartTime = () => {
  return parseInt(sessionStorage.getItem('analytics_session_start') || Date.now().toString());
};

// ============================================================================
// UTM 파라미터 관리 (localStorage 기반 - 30일 유지)
// ============================================================================

const UTM_STORAGE_KEY = 'whatquiz_utm';
const FIRST_UTM_STORAGE_KEY = 'whatquiz_first_utm';
const UTM_EXPIRY_DAYS = 30; // UTM 정보 유효기간 (30일)

// UTM 파라미터 저장 (localStorage, 30일 유지)
const saveUtmParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const utmParams = {
    utm_source: urlParams.get('utm_source'),
    utm_medium: urlParams.get('utm_medium'),
    utm_campaign: urlParams.get('utm_campaign'),
    utm_content: urlParams.get('utm_content'),
    utm_term: urlParams.get('utm_term'),
  };
  
  // 하나라도 있으면 저장 (last-touch: 최신 UTM 덮어쓰기)
  if (Object.values(utmParams).some(v => v)) {
    const utmData = {
      ...utmParams,
      savedAt: Date.now(),
      expiresAt: Date.now() + (UTM_EXPIRY_DAYS * 24 * 60 * 60 * 1000)
    };
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmData));
    
    // first-touch: 최초 UTM은 한 번만 저장
    saveFirstUtmParams(utmParams);
  }
  
  return utmParams;
};

// 최초 유입 UTM 별도 저장 (덮어쓰지 않음)
const saveFirstUtmParams = (utmParams) => {
  try {
    const existing = localStorage.getItem(FIRST_UTM_STORAGE_KEY);
    if (existing) {
      const parsed = JSON.parse(existing);
      if (parsed.expiresAt && Date.now() > parsed.expiresAt) {
        localStorage.removeItem(FIRST_UTM_STORAGE_KEY);
      } else {
        return;
      }
    }
    
    const firstUtmData = {
      ...utmParams,
      landing_page: window.location.pathname,
      referrer: document.referrer || 'direct',
      first_visit: new Date().toISOString(),
      savedAt: Date.now(),
      expiresAt: Date.now() + (90 * 24 * 60 * 60 * 1000)
    };
    
    localStorage.setItem(FIRST_UTM_STORAGE_KEY, JSON.stringify(firstUtmData));
  } catch { /* 조용히 실패 */ }
};

// 최초 유입 UTM 조회
const getFirstUtmParams = () => {
  try {
    const stored = localStorage.getItem(FIRST_UTM_STORAGE_KEY);
    if (!stored) return {};
    
    const data = JSON.parse(stored);
    if (data.expiresAt && Date.now() > data.expiresAt) {
      localStorage.removeItem(FIRST_UTM_STORAGE_KEY);
      return {};
    }
    
    const { savedAt, expiresAt, ...utmParams } = data;
    return utmParams;
  } catch {
    return {};
  }
};

// UTM 파라미터 조회 (만료 체크 포함)
const getUtmParams = () => {
  try {
    const stored = localStorage.getItem(UTM_STORAGE_KEY);
    if (!stored) return {};
    
    const utmData = JSON.parse(stored);
    
    // 만료 체크
    if (utmData.expiresAt && Date.now() > utmData.expiresAt) {
      localStorage.removeItem(UTM_STORAGE_KEY);
      return {};
    }
    
    // 메타데이터 제거하고 UTM만 반환
    const { savedAt, expiresAt, ...utmParams } = utmData;
    return utmParams;
  } catch {
    return {};
  }
};

// UTM 파라미터 초기화 (URL에서 추출 후 저장)
const initUtmParams = () => {
  const urlUtm = saveUtmParams();
  // URL에 UTM이 없으면 기존 저장된 값 반환
  if (!Object.values(urlUtm).some(v => v)) {
    return getUtmParams();
  }
  return urlUtm;
};

export const ensureDefaultCampaignUtm = (campaign, extras = {}) => {
  const normalized = String(campaign || '').trim().toLowerCase();
  if (!/^[a-z0-9][a-z0-9_-]{0,79}$/.test(normalized)) return false;
  initUtmParams();
  const current = getUtmParams();
  if (current.utm_campaign) return false;
  const utmData = {
    utm_source: extras.source || 'event_landing',
    utm_medium: extras.medium || 'event',
    utm_campaign: normalized,
    utm_content: extras.content || null,
    utm_term: extras.term || null,
    savedAt: Date.now(),
    expiresAt: Date.now() + (UTM_EXPIRY_DAYS * 24 * 60 * 60 * 1000),
  };
  try {
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmData));
    if (!localStorage.getItem(FIRST_UTM_STORAGE_KEY)) {
      saveFirstUtmParams({
        utm_source: utmData.utm_source,
        utm_medium: utmData.utm_medium,
        utm_campaign: utmData.utm_campaign,
        utm_content: utmData.utm_content,
        utm_term: utmData.utm_term,
      });
    }
    return true;
  } catch {
    return false;
  }
};

// 내부 링크는 UTM 대신 ?from= 으로 출처를 단다(정적 SEO 페이지 CTA 등). 랜딩 시점 값만 세션 동안 남긴다.
const LANDING_FROM_PATTERN = /^[a-z0-9][a-z0-9_-]{0,79}$/i;

// 첫 방문 페이지 저장
const saveLandingPage = () => {
  if (!sessionStorage.getItem('analytics_landing_page')) {
    sessionStorage.setItem('analytics_landing_page', window.location.pathname);
    sessionStorage.setItem('analytics_referrer', document.referrer || 'direct');
    const from = new URLSearchParams(window.location.search).get('from');
    if (from && LANDING_FROM_PATTERN.test(from)) {
      sessionStorage.setItem('analytics_landing_from', from);
    }
  }
};

const getLandingPage = () => sessionStorage.getItem('analytics_landing_page') || '/';
const getReferrer = () => sessionStorage.getItem('analytics_referrer') || 'direct';
const getLandingFrom = () => sessionStorage.getItem('analytics_landing_from');

// ============================================================================
// 🔒 중복 이벤트 방지 (디바운스)
// ============================================================================

const eventDebounceMap = new Map(); // {eventKey: timestamp}
const DEBOUNCE_MS = 500; // 같은 이벤트 500ms 내 중복 방지

/**
 * 이벤트 중복 체크 (디바운스)
 * @param {string} eventType - 이벤트 타입
 * @param {object} eventData - 이벤트 데이터 (키 생성용)
 * @returns {boolean} - 전송 가능 여부 (true = 전송 OK, false = 중복으로 스킵)
 */
const shouldSendEvent = (eventType, eventData = {}) => {
  // 중복 체크 제외 이벤트 (항상 전송)
  const alwaysSend = ['payment_complete', 'session_end', 'session_start', 'signup_complete'];
  if (alwaysSend.includes(eventType)) return true;
  
  // 이벤트 키 생성 (타입 + 주요 데이터). 학습지·PPT 이벤트는 page·feature 가 없어 타입만 남으면
  // 같은 대화의 한글·PDF 를 연달아 받을 때 두 번째 받기가 빠진다.
  const keyData = {
    type: eventType,
    feature: eventData.feature,
    page: eventData.page_url || eventData.trigger_page,
    conversation: eventData.conversation_id,
    format: eventData.format,
    location: eventData.location,
    scope: eventData.scope,
  };
  // 특성 칩은 0.5초 안에 여러 개를 연달아 누르는 게 정상이라 칩·단계별로 따로 센다.
  if (eventType === 'saenggibu_trait_chip') {
    keyData.chip = eventData.chip_id;
    keyData.stage = eventData.stage;
  }
  const eventKey = JSON.stringify(keyData);
  
  const now = Date.now();
  const lastSent = eventDebounceMap.get(eventKey);
  
  if (lastSent && now - lastSent < DEBOUNCE_MS) {
    return false; // 중복
  }
  
  eventDebounceMap.set(eventKey, now);
  
  // 오래된 키 정리 (메모리 관리)
  if (eventDebounceMap.size > 100) {
    const threshold = now - 60000; // 1분 전
    for (const [key, time] of eventDebounceMap) {
      if (time < threshold) eventDebounceMap.delete(key);
    }
  }
  
  return true;
};

// ============================================================================
// 이벤트 전송 코어
// ============================================================================

/**
 * 이벤트를 백엔드에 전송
 * 
 * ⚠️ 중요: 이 함수는 절대로 사용자 경험에 영향을 주면 안 됨
 * - 모든 에러를 내부에서 처리
 * - 네트워크 실패해도 무시
 * - 타임아웃 짧게 설정
 * 
 * @param {string} eventType - 이벤트 타입
 * @param {object} eventData - 이벤트 데이터
 * @param {object} options - 옵션 (waitForResponse 등)
 */
// 개별 트래커는 isPro를 넘기지 않으면 false로 남겨 왔다. 구독 상태를 아는 SubscriptionContext가
// 알려준 값을 같은 계정의 모든 이벤트 is_pro로 쓴다. 확인 전·비로그인은 호출부 값을 그대로 둔다.
let trackedProStatus = { uid: null, isPro: null };

export const setTrackedProStatus = (uid, isPro) => {
  trackedProStatus = {
    uid: uid || null,
    isPro: uid && typeof isPro === 'boolean' ? isPro : null,
  };
};

const resolveTrackedIsPro = (uid) => (
  uid && trackedProStatus.uid === uid ? trackedProStatus.isPro : null
);

const sendEvent = async (eventType, eventData = {}, options = {}) => {
  // 🛡️ 절대 에러를 외부로 던지지 않음
  try {
    // 🔒 중복 이벤트 방지
    if (!options.skipDedupe && !shouldSendEvent(eventType, eventData)) {
      return true; // 중복이어도 true 반환 (호출자는 정상 처리된 것처럼)
    }
    
    // 안전하게 auth 접근
    let user = null;
    try {
      user = auth?.currentUser || null;
    } catch {
      user = null;
    }
    
    // 🔥 비로그인 유저도 고유 ID로 추적 (anonymous 대신 익명 ID 사용)
    let userId;
    let isAnonymous = false;
    if (user?.uid) {
      userId = user.uid;
    } else {
      // 비로그인 유저: localStorage에 저장된 익명 ID 사용
      try {
        userId = `anon_${getAnonymousUserId()}`;
        isAnonymous = true;
      } catch {
        userId = 'anonymous';
        isAnonymous = true;
      }
    }
    const userEmail = user?.email || null;
    const trackedIsPro = resolveTrackedIsPro(user?.uid);
    
    // UTM 정보 가져오기
    const utmParams = getUtmParams();
    const firstUtm = getFirstUtmParams();
    const landingFrom = getLandingFrom();
    
    // 안전하게 payload 생성
    const payload = {
      event_type: eventType,
      user_id: userId,
      user_email: options.omitUserEmail ? null : userEmail,
      is_anonymous: isAnonymous,
      session_id: getSessionId(),
      timestamp: new Date().toISOString(),
      page_url: window.location?.pathname || '/',
      page_search: window.location?.search || '',
      referrer: getReferrer(),
      landing_page: getLandingPage(),
      // UTM 정보 (last-touch)
      utm: utmParams,
      utm_source: utmParams.utm_source || null,
      utm_medium: utmParams.utm_medium || null,
      utm_campaign: utmParams.utm_campaign || null,
      utm_content: utmParams.utm_content || null,
      utm_term: utmParams.utm_term || null,
      // first-touch UTM (최초 유입 보존)
      first_utm: Object.keys(firstUtm).length > 0 ? firstUtm : null,
      session_duration_seconds: Math.floor((Date.now() - getSessionStartTime()) / 1000),
      user_agent: navigator?.userAgent || 'unknown',
      screen_size: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
      event_data: {
        ...(landingFrom ? { landing_from: landingFrom } : {}),
        ...eventData,
        ...(typeof trackedIsPro === 'boolean' ? { is_pro: trackedIsPro } : {}),
        anonymous_id: (() => { try { return getAnonymousUserId(); } catch { return null; } })(),
      }
    };
    
    let API_BASE_URL = '';
    try {
      API_BASE_URL = getApiBaseUrl();
    } catch {
      // API URL 가져오기 실패하면 조용히 종료
      return true;
    }

    // 페이지 종료 직전 이벤트는 토큰 조회나 fetch가 취소될 수 있다.
    // 공개 수집 엔드포인트에 Beacon으로 보내 마지막 이탈 신호를 보존한다.
    if (options.useBeacon && navigator?.sendBeacon) {
      return navigator.sendBeacon(
        `${API_BASE_URL}/api/analytics/event`,
        JSON.stringify(payload)
      );
    }
    
    // 토큰 가져오기 (실패해도 무시)
    let authHeader = {};
    if (user) {
      try {
        const token = await user.getIdToken();
        authHeader = { 'Authorization': `Bearer ${token}` };
      } catch {
        // 토큰 실패해도 계속 진행 (인증 없이 전송)
      }
    }
    
    // 비동기로 전송 (응답 대기 안함 - 성능 최적화)
    // 🛡️ 짧은 타임아웃 (5초) - 네트워크 느려도 사용자 경험 보호
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    
    if (options.waitForResponse) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/analytics/event`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...authHeader
          },
          body: JSON.stringify(payload),
          signal: controller.signal,
          // 가입 완료처럼 꼭 남겨야 하는 이벤트는 전송 중 화면이 바뀌어도 요청이 끝까지 가게 한다.
          ...(options.reportFailure ? { keepalive: true } : {})
        });
        clearTimeout(timeoutId);
        if (!options.reportFailure) return response.ok;
        // 서버는 저장에 실패해도 200 + success:false 로, 사용자 문서가 아직 없으면 200 + discarded 로 답한다.
        const result = await response.json().catch(() => ({}));
        return response.ok && result?.success !== false && !result?.discarded;
      } catch {
        clearTimeout(timeoutId);
        // 실패해도 true 반환 (사용자 플로우 방해 안 함). reportFailure 는 재시도할 호출자만 쓴다.
        return !options.reportFailure;
      }
    } else {
      // Fire and forget - 완전히 비동기
      fetch(`${API_BASE_URL}/api/analytics/event`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader
        },
        body: JSON.stringify(payload),
        signal: controller.signal
      })
        .then(() => clearTimeout(timeoutId))
        .catch(() => clearTimeout(timeoutId)); // 에러 무시
    }
    
    return true;
  } catch (error) {
    // 🛡️ 모든 에러를 여기서 흡수 - 절대 외부로 던지지 않음
    return false;
  }
};

/**
 * 범용 이벤트 전송. GA4(gtag)와 Firestore analytics를 함께 남긴다.
 * 기능별 전용 트래커가 있으면 그쪽을 우선한다.
 */
export const trackEvent = (eventType, eventData = {}) => {
  if (!eventType || typeof eventType !== 'string') return false;
  try {
    window.gtag?.('event', eventType, eventData);
  } catch {
    // GA 실패는 사용자 흐름을 막지 않는다.
  }
  sendEvent(eventType, eventData);
  return true;
};

const TRAINING_FUNNEL_STAGES = new Set([
  'course_view',
  'preview_started',
  'package_selected',
  'checkout_started',
  'order_created',
  'payment_succeeded',
  'payment_failed',
]);

/**
 * 온라인 자율연수 퍼널. 주문번호·이메일·학교명 없이 과정·패키지 단계만 보낸다.
 */
export const trackTrainingFunnelEvent = (
  stage,
  {
    amount = 0,
    courseSlug = '',
    errorCode = '',
    packageId = '',
  } = {},
) => {
  if (!TRAINING_FUNNEL_STAGES.has(stage)) return false;
  const eventType = `training_${stage}`;
  const eventData = {
    amount: Math.max(Math.round(Number(amount) || 0), 0),
    course_slug: String(courseSlug || '').slice(0, 80),
    error_code: String(errorCode || '').slice(0, 80),
    package_id: String(packageId || '').slice(0, 60),
    funnel_version: 'autonomous_training_v1',
  };
  try {
    window.gtag?.('event', eventType, {
      ...eventData,
      currency: 'KRW',
      event_category: 'training_ecommerce',
    });
  } catch {
    // GA 실패는 결제·수강 흐름을 막지 않는다.
  }
  sendEvent(eventType, eventData);
  return true;
};

// ============================================================================
// 🔴 결제 퍼널 이벤트
// ============================================================================

/**
 * PRO 모달 오픈
 */
export const trackProModalOpen = (feature, trigger = 'unknown') => {
  sendEvent('pro_modal_open', {
    feature,           // 어떤 기능에서 열렸는지 (chat, saenggibu, inspector 등)
    trigger,           // 트리거 (limit_reached, manual_click, etc)
    funnel_step: 1
  });
};

/**
 * PRO 모달 닫기
 */
export const trackProModalClose = (feature, action = 'close') => {
  sendEvent('pro_modal_close', {
    feature,
    action,            // close, outside_click, x_button
    funnel_step: 1
  });
};

/**
 * 무료 체험 버튼 클릭
 */
export const trackFreeTrialClick = (feature) => {
  sendEvent('free_trial_click', {
    feature,
    funnel_step: 2
  });
};

/**
 * 무료 체험 시작 완료
 */
export const trackFreeTrialStart = (feature) => {
  sendEvent('free_trial_start', {
    feature,
    funnel_step: 2
  });
};

/**
 * 할인 말풍선 클릭
 */
export const trackDiscountBubbleClick = (discountType, remainingDays) => {
  sendEvent('discount_bubble_click', {
    discount_type: discountType,  // trial50, renewal20
    remaining_days: remainingDays,
    funnel_step: 3
  });
};

export const trackDiscountBubbleImpression = (discountType, remainingDays) => {
  sendEvent('discount_bubble_impression', {
    discount_type: discountType,
    remaining_days: remainingDays,
    surface: 'navigation',
    funnel_step: 3,
  }, { omitUserEmail: true });
};

/**
 * 마이페이지 개인 할인 카드 클릭
 */
export const trackPersonalOfferClick = (offerId, daysLeft) => {
  sendEvent('personal_offer_click', {
    offer_id: offerId,
    source: 'account_offer',
    days_left: daysLeft,
    funnel_step: 3
  });
};

/**
 * 결제 페이지 진입
 */
export const trackPaymentPageView = (source, plan, price, promoCode = null) => {
  sendEvent('payment_page_view', {
    source,            // pro_modal, discount_bubble, pricing_page, direct, etc
    plan,              // 3month, 12month
    price,
    promo_code: promoCode,
    funnel_step: 4
  });
};

/**
 * 결제 시작 (토스 위젯 오픈)
 */
export const trackPaymentStart = (plan, originalPrice, finalPrice, promoCode = null) => {
  sendEvent('payment_start', {
    plan,
    original_price: originalPrice,
    final_price: finalPrice,
    promo_code: promoCode,
    discount_amount: originalPrice - finalPrice,
    discount_rate: Math.round((1 - finalPrice / originalPrice) * 100),
    funnel_step: 5
  });
};

/**
 * 결제 완료
 */
export const trackPaymentComplete = (orderId, plan, amount, promoCode = null, daysSinceSignup = null) => {
  sendEvent('payment_complete', {
    order_id: orderId,
    plan,
    amount,
    promo_code: promoCode,
    days_since_signup: daysSinceSignup,
    funnel_step: 6,
    is_conversion: true
  }, { waitForResponse: true });  // 중요한 이벤트는 응답 대기
};

/**
 * 결제 실패
 */
export const trackPaymentFailed = (plan, amount, errorCode, errorMessage) => {
  sendEvent('payment_failed', {
    plan,
    amount,
    error_code: errorCode,
    error_message: errorMessage,
    funnel_step: 5
  });
};

/**
 * 결제 페이지에서 결제창 준비(SDK 로드 → 주문 생성 → 토스 초기화)가 실패해 버튼이 막힘. 오류 문구는 보내지 않는다.
 * stage: sdk_load | create_order | toss_init
 */
export const trackPaymentInitFailed = (plan, stage, errorName, httpStatus = null, attempt = 1) => {
  sendEvent('payment_init_failed', {
    plan,
    stage,
    error_name: String(errorName || 'Error').replace(/[^A-Za-z0-9_]/g, '').slice(0, 40) || 'Error',
    http_status: Number.isInteger(httpStatus) ? httpStatus : null,
    attempt,
    funnel_step: 4
  });
};

/**
 * 결제창/카드 등록창을 사용자가 직접 닫음. 결제 실패(payment_failed)와 분리해 실패율을 왜곡하지 않는다.
 * source: checkout_window(PC 결제창 닫기) | billing_redirect(모바일 등 실패 URL로 돌아옴)
 */
export const trackPaymentCancelled = (plan, amount, source) => {
  sendEvent('payment_cancelled', {
    plan,
    amount,
    source,
    funnel_step: 5
  });
};

/**
 * 결제 이탈 (결제 페이지에서 나감)
 */
export const trackPaymentAbandoned = (
  plan,
  price,
  promoCode = null,
  timeSpentSeconds = 0,
  reason = 'navigation'
) => {
  sendEvent('payment_abandoned', {
    plan,
    price,
    promo_code: promoCode,
    time_spent_seconds: timeSpentSeconds,
    abandon_reason: reason,
    funnel_step: 4
  }, { useBeacon: true });
};

// ============================================================================
// 🟢 회원가입 퍼널 이벤트
// ============================================================================

/**
 * 회원가입 페이지 진입
 */
export const trackSignupPageView = (source = 'direct') => {
  sendEvent('signup_page_view', {
    source,
    funnel_step: 1
  });
};

/**
 * 회원가입 시도 (버튼 클릭)
 */
export const trackSignupAttempt = (method) => {
  sendEvent('signup_attempt', {
    method,   // google, email
    funnel_step: 2
  });
};

/**
 * 회원가입 완료
 */
export const trackSignupComplete = (method, source = null, sendOptions = {}) => {
  // UTM과 랜딩페이지 정보 포함
  const utmParams = getUtmParams();
  
  return sendEvent('signup_complete', {
    method,
    source,
    landing_page: getLandingPage(),
    ...utmParams,
    funnel_step: 3,
    is_conversion: true
  }, { waitForResponse: true, ...sendOptions });
};

const SIGNUP_TRACKED_KEY_PREFIX = 'wq_signup_complete_tracked:';

const SIGNUP_RETRY_DELAY_MS = 3000;

const SIGNUP_PENDING_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

const signupInFlight = new Set();
const signupFallbacks = new Map();

const signupMarkKey = (uid) => `${SIGNUP_TRACKED_KEY_PREFIX}${uid}`;

const readSignupMark = (uid) => {
  let raw = null;
  try {
    raw = localStorage.getItem(signupMarkKey(uid));
  } catch {
    // localStorage 를 못 쓰면 매번 보내고 중복은 서버에 맡긴다.
    return null;
  }
  if (!raw) return null;
  try {
    const mark = JSON.parse(raw);
    // 예전 형식(보낸 시각 숫자)은 이미 보낸 것으로 본다.
    return mark && typeof mark === 'object' && mark.state ? mark : { state: 'done' };
  } catch {
    return { state: 'done' };
  }
};

const writeSignupMark = (uid, mark) => {
  try { localStorage.setItem(signupMarkKey(uid), JSON.stringify(mark)); } catch { /* 무시 */ }
};

/**
 * 같은 계정의 가입 완료를 한 번만 남긴다.
 * AuthPage(Google 팝업 결과)와 서버 초기화 응답(is_new_user) 두 곳에서 부르며, 먼저 도착한 쪽만 기록된다.
 * Google 가입은 팝업 결과보다 로그인 상태 감지 쪽 이동이 먼저 끝나면 AuthPage 기록이 빠졌다(2026-09 신규 Google 계정 85% 누락).
 * 서버 저장이 확인되기 전까지는 'pending' 으로 두어, 전송 중 화면이 바뀌어도 다음 화면에서 이어 보낸다
 * (resumePendingSignupComplete). 중복 전송은 서버가 계정당 1건으로 막는다.
 */
export const trackSignupCompleteOnce = (uid, method, source, attempt = 1) => {
  if (!uid) return false;
  if (signupInFlight.has(uid)) {
    // 이 탭에서 다른 경로가 보내는 중이면, 그 전송이 끝내 실패했을 때 대신 보낼 수 있게 남겨 둔다.
    if (!signupFallbacks.has(uid)) signupFallbacks.set(uid, { method, source });
    return false;
  }
  const mark = readSignupMark(uid);
  if (mark?.state === 'done') return false;
  writeSignupMark(uid, { state: 'pending', method, source, at: mark?.at || Date.now() });
  signupInFlight.add(uid);
  Promise.resolve(trackSignupComplete(method, source, { reportFailure: true }))
    .then((saved) => {
      if (saved) {
        signupInFlight.delete(uid);
        signupFallbacks.delete(uid);
        writeSignupMark(uid, { state: 'done', at: Date.now() });
        return;
      }
      // 실패해도 pending 표시는 남겨 재시도·다음 화면에서 다시 보낼 수 있게 한다.
      // 막혀 있던 다른 경로(대개 사용자 문서가 생긴 뒤의 server_init)가 있으면 그 정보로 다시 보낸다.
      let next = null;
      if (attempt < 2) {
        next = { method, source, attempt: attempt + 1 };
      } else if (signupFallbacks.has(uid)) {
        next = { ...signupFallbacks.get(uid), attempt: 1 };
        signupFallbacks.delete(uid);
      }
      signupInFlight.delete(uid);
      if (next) {
        setTimeout(
          () => trackSignupCompleteOnce(uid, next.method, next.source, next.attempt),
          SIGNUP_RETRY_DELAY_MS
        );
      }
    });
  return true;
};

/**
 * 이전 화면에서 저장 확인 전에 끊긴 가입 완료가 있으면 같은 정보로 다시 보낸다.
 * 로그인된 사용자 초기화가 끝날 때마다 부른다(서버 문서가 있으니 저장된다).
 */
export const resumePendingSignupComplete = (uid) => {
  if (!uid || signupInFlight.has(uid)) return false;
  const mark = readSignupMark(uid);
  if (mark?.state !== 'pending') return false;
  if (!mark.at || Date.now() - mark.at > SIGNUP_PENDING_MAX_AGE_MS) {
    try { localStorage.removeItem(signupMarkKey(uid)); } catch { /* 무시 */ }
    return false;
  }
  return trackSignupCompleteOnce(uid, mark.method, mark.source);
};

/**
 * 첫 기능 사용
 */
export const trackFirstFeatureUse = (feature, daysSinceSignup = 0) => {
  sendEvent('first_feature_use', {
    feature,
    days_since_signup: daysSinceSignup,
    funnel_step: 4
  });
};

// ============================================================================
// 🟣 로그인 모달 이벤트 (비로그인 → 회원가입 퍼널)
// ============================================================================

/**
 * 로그인 모달 오픈
 * 비로그인 유저가 기능 사용 시도 시 모달이 열림
 */
export const trackLoginModalOpen = (feature, page) => {
  sendEvent('login_modal_open', {
    trigger_feature: feature,  // chat, saenggibu, inspector, lab, scoring
    trigger_page: page,
    funnel_type: 'signup',
    funnel_step: 0  // 회원가입 퍼널 시작
  });
};

/**
 * 로그인 모달 닫기 (이탈)
 */
export const trackLoginModalClose = (feature, page, closeType = 'close_button') => {
  sendEvent('login_modal_close', {
    trigger_feature: feature,
    trigger_page: page,
    close_type: closeType,  // close_button, background_click
    funnel_type: 'signup',
    is_abandonment: true
  });
};

/**
 * 로그인 모달에서 회원가입/로그인 버튼 클릭
 */
export const trackLoginModalSignupClick = (feature, page, couponStatus = 'no_coupon') => {
  sendEvent('login_modal_signup_click', {
    trigger_feature: feature,
    trigger_page: page,
    coupon_status: couponStatus,  // with_coupon, no_coupon
    funnel_type: 'signup',
    funnel_step: 0.5  // 회원가입 페이지로 이동 직전
  });
};

// ============================================================================
// 🔵 기능 사용 이벤트
// ============================================================================

/**
 * 기능 사용 시작
 */
export const trackFeatureUse = (feature, mode = 'single', isPro = false) => {
  sendEvent('feature_use', {
    feature,     // chat, saenggibu, inspector, scoring, lab
    mode,        // single, batch
    is_pro: isPro
  });
};

/**
 * 기능 사용 완료
 */
export const trackFeatureComplete = (feature, mode = 'single', isPro = false, resultCount = 1) => {
  sendEvent('feature_complete', {
    feature,
    mode,
    is_pro: isPro,
    result_count: resultCount
  });
};

const CHAT_UX_ACTIONS = new Set([
  'view',
  'category_select',
  'model_select',
  'send',
  'first_content',
  'complete',
  'stop',
  'retry',
  'error',
  // 🚫 이미지 안전·저작권 거절. 서버 오류가 아니라서 chat_error와 따로 센다.
  'image_refused',
  // 💾 대화 기록 저장 실패(재시도까지 실패). stage 에 create_conversation·save_user·save_ai·update_conversation.
  'save_failed',
  // 🧠 AI 기억 제안 카드 (노출 → 기억하기/이번만 → 실행취소)
  'memory_suggestion_shown',
  'memory_suggestion_accepted',
  'memory_suggestion_dismissed',
  'memory_suggestion_undone',
  // 🧠 기억 보이기: 빈 화면 'AI가 기억하는 것 N개' 줄·답변 아래 '기억해 두었어요' 한 줄 노출 → 관리 이동. sourceCount 에 개수만.
  'memory_entry_shown',
  'memory_entry_clicked',
  'memory_chip_shown',
  'memory_chip_clicked',
  // 🧩 답변 부품(카드·차트·복사 상자 등)을 그리다 실패해 글로 대신 보임. stage 에 부품 id.
  'answer_part_fallback',
  // 🧭 빈 화면 개인화 (K-2): 프로필 기반 인사말 노출, 30초 설문 안내 카드 노출 → 설정하기/나중에
  'personalized_greeting_shown',
  'survey_nudge_shown',
  'survey_nudge_clicked',
  'survey_nudge_dismissed',
  // 🗓️ 채팅 진입 D-day 제안 (학교 연결 교사만): 노출 → 탭 / 오늘은 그만 보기. category 에 착지 카테고리, count 에 개수만.
  'dday_suggestion_shown',
  'dday_suggestion_clicked',
  'dday_suggestion_more',
  'dday_suggestion_dismissed',
  // 📰 채팅 진입 '오늘의 교육 뉴스' 한 줄: 노출 → 열기 / 메일로 받기 / 30일 숨기기
  'news_card_shown',
  'news_card_clicked',
  'news_card_subscribe_clicked',
  'news_card_dismissed',
  // 📐 이미지 메뉴의 수학 도형·수치 그래프 안내(채팅에서 정확하게 그리기): 노출 → 이동 / 닫기. entry 에 이미지 목적.
  'image_figure_hint_shown',
  'image_figure_hint_clicked',
  'image_figure_hint_dismissed',
  // 📐 이미지 요청을 정확한 도형·그래프로 그림 → 그 그림을 꾸민 그림으로 다시 그리기. figure_kind 에 도형 종류.
  'image_exact_figure',
  'image_exact_figure_restyle',
  // 🧭 답변 끝 카테고리 이어가기 카드 (Phase 2): 노출 → 이어가기 / 괜찮아요. entry 에 대상 카테고리를 담는다.
  'handoff_suggestion_shown',
  'handoff_suggestion_clicked',
  'handoff_suggestion_dismissed',
  // 🎯 P6 결과 아래 다음 도구 카드: 노출 → 수락 / 괜찮아요. entry 에 '<출발>-><대상>'.
  'next_tool_nudge_shown',
  'next_tool_nudge_accepted',
  'next_tool_nudge_dismissed',
  // 📎 첨부 거절 칩, 업로드 끝나면 자동 전송 대기열, 자동 스킬 라벨 '바꾸기'
  'attachment_rejected',
  'send_queued_for_upload',
  'skill_label_changed',
  // 🔁 마지막 답변 다시 만들기. prev_chars 에 바꾸기 전 답변 글자 수(원문 없음).
  'answer_regenerate',
  // ✏️ 마지막 내 메시지 고쳐 보내기. 고친 뒤·앞 글자 수와 바꾼 답변 글자 수만(원문 없음).
  'message_edit_resend',
  // P2/P3 산출물 제안 카드(이미지/PPT/학습지, 이 대화 안에서 생성). entry = `${kind}:${explicitness|card|auto}`
  'artifact_proposal_shown',
  'artifact_proposal_accepted',
  'artifact_proposal_dismissed',
  'inline_image_generated',
  'inline_image_failed',
  // P4 "편집 중" 칩으로 대상 산출물 전환. entry = 새 kind
  'artifact_target_switched',
  // 🗂️ 첨부 파일 자료함 저장 제안 (Phase 3): 노출 → 저장/PRO 알아보기 / 나중에
  'library_suggestion_shown',
  'library_suggestion_accepted',
  'library_suggestion_dismissed',
  'ai_reference_consent_shown',
  'ai_reference_consent_allowed',
  'ai_reference_consent_declined',
  // 📂 채팅 입력창에 폴더 놓기 → 자료함에 담기. stage = ready|consent|category|empty, done|stopped
  'library_folder_found',
  'library_folder_started',
  'library_folder_finished',
  // 🗂️ 답 아래 '지난 대화 … 참고': 읽은 대화 열기, '참고 안 함'(stage = excluded|restored)
  'past_chat_reference_opened',
  'past_chat_reference_excluded',
  // 🃏 첫 턴 되묻기 카드: 노출 → 답변/수정/스킵/유휴 만료, 가정 칩 바꾸기
  'clarify_card_shown',
  'clarify_card_answered',
  'clarify_card_edited',
  'clarify_card_skipped',
  'clarify_card_timer_expired',
  'clarify_assumptions_shown',
  'clarify_assumption_edit_clicked',
  // 📎 파일 약속만 하고 파일이 없던 답변에서 '지금 파일로 만들기'를 누름
  'promise_without_file_make_now',
  // ✏️ 첨부 파일 수정이 반영되지 않은 답변에서 '지금 반영하기'를 누름
  'edit_not_applied_apply_now',
  // ⏳ 만들겠다고만 하고 실행 신호가 없던 답변에서 '지금 바로 하기'를 누름
  'action_not_taken_do_now',
  // 🎙️ 음성 입력·듣기 (녹음·글 원문은 보내지 않는다): 받아쓰기 결과, 보낼 때 고친 비율, 듣기 첫 소리·끝 상태
  'voice_transcribe',
  'voice_send',
  'voice_speak',
  // v1.1: 요청당 1장 규칙으로 서버가 자동 처리한 카드 / 학습된 기본값 '바꾸기'
  'clarify_card_auto_resolved',
  'clarify_learned_default_cleared',
  // 📎 채팅 파일 생성 실패 (file_artifact_result success:false). error_code 에 서버 예외 이름,
  //    entry 에 형식(hwpx/pdf/…)을 담는다. 이전엔 토스트로만 사라져 측정이 불가능했다.
  'artifact_failed',
  // 📎 파일을 만들기 전에 정보가 더 필요하거나(needs_clarification) 로그인이 필요(auth_required)한 정상 안내.
  //    실패율을 부풀리지 않도록 artifact_failed 와 분리한다.
  'artifact_needs_input',
  // 🖼️ 인라인 이미지 스트립: 노출(source_count=이미지 수) → 타일 클릭(뷰어) → 출처 열기 → 불러오기 실패로 숨김
  'image_strip_shown',
  'image_strip_opened',
  'image_strip_source_clicked',
  'image_strip_hidden',
  // 📊 답변 속 차트(wq-chart): 노출(source_count=항목 수, stage=bar|line) / '표로 보기' / 데이터가 틀려 못 그림(error_code)
  'chart_shown',
  'chart_table_opened',
  'chart_invalid',
  // 💻 코드블록·HTML 미리보기. entry=auto|button|inline|codeblock|panel, stage=quick_close(자동으로 열린 뒤 5초 안에 닫음) 등
  'code_copy',
  'html_download',
  'html_preview_open',
  // 📐 휴대폰 답변 안에 그림 HTML 을 바로 그림(그림 블록마다 1회)
  'html_figure_inline',
  'html_preview_close',
  'html_preview_version',
  'html_preview_fix_errors',
  // ⬇️ 오른쪽 미리보기 패널 다운로드. entry=original|current|hwp_save, stage=success|failed, format=확장자
  'panel_download',
  // 📖 답변 읽기: 답변 복사·공유, 출처 펼침·출처 링크 클릭(source_count=출처 수), 넓은 표 가로 스크롤(답변당 표마다 1회)
  'message_copy',
  'share_clicked',
  'sources_opened',
  'citation_click',
  'table_hscroll',
  // 📊 표 하나를 엑셀 파일로 받음. table_cols·table_rows 만(칸 글자 없음)
  'table_download',
  // 🃏 사진 추천 카드: 보임(source_count=사진 수), 썸네일·사진 출처 클릭(entry=thumb|source)
  'rich_cards_shown',
  'rich_card_click',
  // 🧭 답변 끝 조건 고르기 양식: 보임(source_count=문항 수), 제출(source_count=고른 문항 수)
  'followup_form_shown',
  'followup_form_submit',
  // ⏭️ 답변 아래 다음 질문 칩: 보임(source_count=칩 수, finish_count=마무리 칩 수), 누름(chip_index, chip_kind). 칩 글은 남기지 않는다.
  'followup_chip_shown',
  'followup_chip_click',
  // 🧩 대화 속 퀴즈·플래시카드: 보임(source_count=문항·카드 수, stage=quiz|flashcards|*_plain) / 형식이 틀려 글로 보임(error_code)
  //    / 다 풂(source_count=문항 수, correct_count) / 다시 풀기 / 카드를 처음 뒤집음
  'inchat_quiz_shown',
  'inchat_quiz_invalid',
  'inchat_quiz_completed',
  'inchat_quiz_retry',
  'inchat_flashcards_flip',
  // 🎓 '수업 자료로' 메뉴: 보임 / 펼침 / 고름(entry=quiz|flashcards|worksheet|ppt)
  'answer_material_shown',
  'answer_material_open',
  'answer_material_select',
  // 📐 정확한 도형 그리기 / 그린 도형 다시 꾸미기 (화이트리스트 누락으로 버려지던 이벤트)
  'image_exact_figure',
  'image_exact_figure_restyle',
  // 🌙 화면 모드·단순하게 보기: 바꿈(entry=settings), 기본값이 아닌 모드로 /chat 을 연 세션 1회(appearance_active)
  'appearance_theme_change',
  'appearance_simple_change',
  'appearance_active',
]);

const CLASSROOM_BUNDLE_ACTIONS = new Set([
  'landing_viewed',
  'purpose_selected',
  'generation_started',
  'generation_completed',
  'generation_failed',
  // 무료 사용량 소진은 실패가 아니라 전환 지점이라 generation_failed와 분리한다.
  'quota_reached',
  'handoff_clicked',
  'handoff_consumed',
  'artifact_completed',
  'share_clicked',
  'shared_result_viewed',
  'recipient_started',
  'download_clicked',
  'account_gate_viewed',
  'signup_clicked',
  'trial_started',
]);

const CLASS_CAMPAIGN_EVENT_ACTIONS = new Set([
  'landing_viewed',
  'generation_started',
  'generation_completed',
  'generation_failed',
  'quota_reached',
  'share_clicked',
  'shared_result_viewed',
  'recipient_started',
  'cross_handoff_clicked',
  'cross_handoff_landed',
  'handoff_clicked',
  'handoff_consumed',
  'library_clicked',
  'download_clicked',
  'account_gate_viewed',
  'signup_clicked',
  'trial_started',
  // 첨부 신뢰성(P4): 로컬 검증 거절 / 업로드 중 전송 대기열
  'attachment_rejected',
  'send_queued_for_upload',
]);

const sanitizeChatUxToken = (value, fallback = 'unknown') => {
  const sanitized = String(value || '')
    .trim()
    .slice(0, 60)
    .replace(/[^a-zA-Z0-9._-]/g, '_');
  return sanitized || fallback;
};

const clampChatUxNumber = (value, maximum) => (
  Math.min(Math.max(Math.round(Number(value) || 0), 0), maximum)
);

const getChatLatencyBucket = (elapsedMs) => {
  if (elapsedMs < 2000) return 'under_2s';
  if (elapsedMs < 5000) return '2_5s';
  if (elapsedMs < 10000) return '5_10s';
  if (elapsedMs < 30000) return '10_30s';
  if (elapsedMs < 60000) return '30_60s';
  return 'over_60s';
};

const getChatViewportGroup = () => {
  const width = Number(window?.innerWidth || 0);
  if (width <= 768) return 'mobile';
  if (width <= 1024) return 'tablet';
  return 'desktop';
};

/**
 * `/chat` 사용성 퍼널 이벤트.
 *
 * prompt, 파일명, conversationId 등 원문·식별 정보는 받더라도 버리고,
 * 여기서 명시한 비식별 집계 필드만 GA4와 Firestore에 전달한다.
 */
export const trackChatUxEvent = (action, metadata = {}) => {
  if (!CHAT_UX_ACTIONS.has(action)) return false;

  const elapsedMs = clampChatUxNumber(metadata.elapsedMs, 3_600_000);
  const eventType = `chat_${action}`;
  const eventData = {
    action,
    artifact_count: clampChatUxNumber(metadata.artifactCount, 100),
    category: sanitizeChatUxToken(metadata.category, 'general'),
    elapsed_ms: elapsedMs,
    entry: sanitizeChatUxToken(metadata.entry, 'unknown'),
    error_code: sanitizeChatUxToken(metadata.errorCode, 'none'),
    file_count: clampChatUxNumber(metadata.fileCount, 20),
    is_anonymous: metadata.isAnonymous === true,
    is_pro: metadata.isPro === true,
    latency_bucket: getChatLatencyBucket(elapsedMs),
    message_count: clampChatUxNumber(metadata.messageCount, 10_000),
    model: sanitizeChatUxToken(metadata.model, 'unknown'),
    response_chars: clampChatUxNumber(metadata.responseChars, 1_000_000),
    source_count: clampChatUxNumber(metadata.sourceCount, 100),
    stage: sanitizeChatUxToken(metadata.stage, 'none'),
    viewport_group: getChatViewportGroup(),
  };
  if (metadata.effectiveModel) {
    eventData.effective_model = sanitizeChatUxToken(metadata.effectiveModel);
  }
  if (metadata.conversationId) {
    eventData.conversation_id = sanitizeChatUxToken(metadata.conversationId, 'none');
  }
  if (metadata.format) {
    const format = String(metadata.format).trim().toLowerCase();
    eventData.format = /^[a-z0-9]{1,5}$/.test(format) ? format : 'other';
  }
  // 📋 message_copy 가 무엇을 복사했는지: answer(답변 전체) | table(표 하나) | block(복사용 글 카드) | quiz·flashcards(대화 속 퀴즈·카드)
  if (action === 'message_copy') {
    eventData.kind = ['answer', 'table', 'block', 'quiz', 'flashcards'].includes(metadata.kind) ? metadata.kind : 'answer';
  }
  if (action === 'inchat_quiz_completed') {
    eventData.correct_count = clampChatUxNumber(metadata.correctCount, 100);
  }
  if (action === 'table_download') {
    eventData.table_cols = clampChatUxNumber(metadata.cols, 100);
    eventData.table_rows = clampChatUxNumber(metadata.rows, 10_000);
  }
  // ⏹️ 답변을 받는 중 멈춤: 그때까지 받은 글자 수(원문 없음). 이미지 모드 멈춤은 보내지 않는다.
  if (action === 'stop' && metadata.charsReceived !== undefined) {
    eventData.chars_received = clampChatUxNumber(metadata.charsReceived, 1_000_000);
  }
  if ((action === 'answer_regenerate' || action === 'message_edit_resend') && metadata.prevChars !== undefined) {
    eventData.prev_chars = clampChatUxNumber(metadata.prevChars, 1_000_000);
  }
  if (action === 'message_edit_resend') {
    if (metadata.editedChars !== undefined) eventData.edited_chars = clampChatUxNumber(metadata.editedChars, 100_000);
    if (metadata.originalChars !== undefined) eventData.original_chars = clampChatUxNumber(metadata.originalChars, 100_000);
  }
  if (action === 'followup_chip_shown') {
    eventData.finish_count = clampChatUxNumber(metadata.finishCount, 10);
  }
  if (action === 'followup_chip_click') {
    eventData.chip_index = clampChatUxNumber(metadata.index, 10);
    eventData.chip_kind = metadata.kind === 'finish' ? 'finish' : 'followup';
  }
  // 🃏 되묻기 카드: 어떤 경로의 카드인지(source), 어떤 모드(mode), 무엇을 물었는지(keys), 몇 개 답했는지
  if (action.startsWith('clarify_')) {
    if (metadata.source) eventData.card_source = sanitizeChatUxToken(metadata.source);
    if (metadata.mode) eventData.card_mode = sanitizeChatUxToken(metadata.mode);
    const keys = String(metadata.keys || metadata.key || '')
      .split(',')
      .map((key) => key.trim())
      .filter(Boolean)
      .slice(0, 8)
      .map((key) => sanitizeChatUxToken(key).slice(0, 30));
    if (keys.length > 0) eventData.card_keys = keys.join(',');
    if (metadata.answeredCount !== undefined) eventData.answered_count = clampChatUxNumber(metadata.answeredCount, 20);
  }
  if (action.startsWith('appearance_')) {
    eventData.theme_setting = ['light', 'dark', 'system'].includes(metadata.themeSetting) ? metadata.themeSetting : 'light';
    eventData.theme_effective = metadata.themeEffective === 'dark' ? 'dark' : 'light';
    eventData.simple_view = metadata.simpleView === true;
  }
  if (action.startsWith('image_exact_figure') && metadata.figureKind) {
    eventData.figure_kind = sanitizeChatUxToken(metadata.figureKind);
  }
  if (action.startsWith('voice_')) {
    if (metadata.engine) eventData.voice_engine = sanitizeChatUxToken(metadata.engine);
    if (metadata.outcome) eventData.voice_outcome = sanitizeChatUxToken(metadata.outcome);
    if (metadata.editRatio !== undefined) {
      eventData.edit_ratio = Math.round(Math.min(Math.max(Number(metadata.editRatio) || 0, 0), 1) * 100) / 100;
    }
    if (metadata.recordMs !== undefined) eventData.record_ms = clampChatUxNumber(metadata.recordMs, 600_000);
    if (metadata.firstAudioMs !== undefined) eventData.first_audio_ms = clampChatUxNumber(metadata.firstAudioMs, 600_000);
    if (metadata.segmentCount !== undefined) eventData.segment_count = clampChatUxNumber(metadata.segmentCount, 100);
    if (metadata.voiceChars !== undefined) eventData.voice_chars = clampChatUxNumber(metadata.voiceChars, 100_000);
    if (metadata.auto !== undefined) eventData.auto = metadata.auto === true;
  }

  try {
    window.gtag?.('event', eventType, {
      ...eventData,
      event_category: 'chat_ux',
    });
  } catch {
    // GA 실패는 채팅 흐름을 막지 않는다.
  }
  sendEvent(eventType, eventData);
  return true;
};

// ============================================================================
// 학습지·PPT 전면 개선 계측 (WP-0) — docs/ws_ppt_overhaul_contracts_2026_09.md §4
// ============================================================================

/** 고정 action 목록. 이외 값은 무시하고 false를 돌려준다. */
export const WS_PPT_ACTIONS = Object.freeze([
  'outline_generated',
  'outline_regenerated',
  'mode_selected',
  'canvas_opened',
  'canvas_saved',
  'artifact_saved',
  'artifact_downloaded',
  'resume_edit',
  'version_restored',
  'regen_requested',
  'regen_diff',
  // 텍스트형 PPT(자유 디자인) — FreeDesignDeck onTrack
  'free_design_started',
  'free_design_completed',
  'free_design_failed',
  'free_design_stopped',
  'free_design_slide_moved',
  'free_design_slide_deleted',
  'free_design_redesign',
  'free_design_chat_sync',
  'free_design_export',
  'free_design_to_image',
  'free_design_text_edit',
  'free_design_element_move',
  'free_design_element_delete',
  'free_design_element_undo',
  // 만드는 도중 떠났다 돌아왔을 때 서버가 마저 만든 덱 이어받기
  'free_design_resume_wait',
  'free_design_resumed',
  'free_design_resume_timeout',
  // 모델이 태그를 빠뜨리거나 잘못 골라 안전장치가 바로잡음
  'chat_action_guard',
  // 파일 자동 생성·재사용, 그림 PPT 쪽 정리, 직접 고친 한글 파일과 다시 만들기 충돌
  'pptx_auto_generated',
  'text_pptx_reused',
  'image_slides_reordered',
  'image_slide_deleted',
  'hwpx_auto_generated',
  'hwpx_rerender_conflict',
  'hwpx_rerender_conflict_choice',
  // 이미지형 생성 시작·끝, 이미지로 해 달라는 요청을 화면이 어떻게 처리했는지
  'image_generate_started',
  'image_generate_finished',
  'image_request_resolved',
  // 다른 대화의 학습지·PPT 를 참고해 만들기([ARTIFACT_REF])
  'artifact_ref_used',
  'artifact_ref_missing',
  // 텍스트형 PPT 발표 모드 열기 — 파일을 받지 않고 바로 수업에 쓰는 경우
  'presentation_opened',
  // 채팅 AI 가 도구로 초안을 고치거나(edit) 만들기를 시작(create)한 결과를 화면이 어떻게 처리했는지
  'chat_tool_result',
]);
const CHAT_ACTION_GUARDS = new Set(['promise_without_tag', 'confirm_after_design', 'design_only_auto']);
const WS_PPT_ACTION_SET = new Set(WS_PPT_ACTIONS);
const FREE_DESIGN_SYNC_SCOPES = new Set(['all', 'slides']);
const WS_PPT_MODES = new Set(['text', 'image', 'hybrid']);
const WS_PPT_FORMATS = new Set(['pdf', 'pptx', 'pptx_editable', 'docx', 'hwpx', 'png']);
const WS_PPT_RESUME_SOURCES = new Set(['conversation', 'library', 'message']);
// 받기 위치: 결과 카드 / 크게 보기 / 미리보기 머리 저장 버튼 / 채팅으로 "파일로 줘"
//   / 내 자료(라이브러리) / 이미지형 완성 직후 자동 받기 / 지난 버전 카드
const WS_PPT_DOWNLOAD_LOCATIONS = new Set(['card', 'lightbox', 'header', 'chat_tag', 'library', 'auto', 'version_card']);
const CHAT_TOOL_KINDS = new Set(['edit', 'create']);
// applied: 초안에 반영 / rolled_back: 다른 항목이 바뀌어 원본 유지 / no_change: 바뀐 것 없음
// stale: 그사이 초안이 바뀌어 적용하지 않음 / failed: 오류
const CHAT_TOOL_OUTCOMES = new Set(['applied', 'partial', 'rolled_back', 'no_change', 'stale', 'failed', 'started']);
// pages = 실패한 쪽만 다시 만들기(WP-H regenerate-pages / regenerate-slides)
const WS_PPT_REGEN_SCOPES = new Set(['all', 'item', 'patch', 'pages', 'style', 'region']);

const wsPptEnum = (value, allowed, fallback) => {
  const token = String(value || '').trim().toLowerCase();
  return allowed.has(token) ? token : fallback;
};

/**
 * 학습지/PPT 공통 이벤트 조립·전송.
 * 원문(프롬프트, 제목, 파일명)은 받아도 버린다. conversation_id는 내부 분석용이라 그대로 보낸다.
 * @returns {boolean} 전송 여부 (알 수 없는 action이면 false)
 */
const trackWsPptEvent = (prefix, category, action, data = {}) => {
  if (!WS_PPT_ACTION_SET.has(action)) {
    try { console.warn(`[eventTracker] ${prefix}_ 알 수 없는 action:`, action); } catch { /* noop */ }
    return false;
  }
  const meta = data && typeof data === 'object' ? data : {};
  const eventType = `${prefix}_${action}`;
  const eventData = {
    action,
    category,
    conversation_id: sanitizeChatUxToken(meta.conversationId ?? meta.conversation_id, 'none'),
    source_mode: wsPptEnum(meta.sourceMode ?? meta.source_mode ?? meta.mode, WS_PPT_MODES, 'unknown'),
    is_anonymous: meta.isAnonymous === true,
    is_pro: meta.isPro === true,
    viewport_group: getChatViewportGroup(),
    // 한 대화 안에서 여러 산출물이 만들어지는지(원스톱 흐름) 보기 위한 두 필드.
    // source_category: 요청 당시 헤더 카테고리(ppt_maker/worksheet_gen 이 아닌 곳에서 나오면 교차 생성).
    // conversation_artifact_count: 이 대화에 이미 있는 결과물 수(PPT+학습지 합, 이번 것 제외).
    source_category: sanitizeChatUxToken(meta.sourceCategory ?? meta.source_category, 'unknown'),
    conversation_artifact_count: clampChatUxNumber(
      meta.artifactCount ?? meta.conversation_artifact_count,
      100,
    ),
  };

  switch (action) {
    case 'outline_generated':
    case 'outline_regenerated': {
      eventData.item_count = clampChatUxNumber(meta.itemCount ?? meta.item_count, 500);
      const elapsedMs = clampChatUxNumber(meta.elapsedMs ?? meta.elapsed_ms, 3_600_000);
      eventData.elapsed_ms = elapsedMs;
      eventData.latency_bucket = getChatLatencyBucket(elapsedMs);
      eventData.trigger_kind = sanitizeChatUxToken(meta.triggerKind ?? meta.trigger_kind, 'unknown');
      break;
    }
    case 'mode_selected':
      eventData.mode = wsPptEnum(meta.mode, WS_PPT_MODES, 'unknown');
      eventData.source = sanitizeChatUxToken(meta.source, 'sheet');
      break;
    case 'artifact_ref_used':
    case 'artifact_ref_missing':
      eventData.source_kind = sanitizeChatUxToken(meta.sourceKind, 'unknown');
      eventData.target_kind = sanitizeChatUxToken(meta.targetKind, 'unknown');
      if (action === 'artifact_ref_missing') eventData.found = meta.found === true;
      break;
    case 'presentation_opened':
      eventData.page_count = clampChatUxNumber(meta.slideCount ?? meta.pageCount, 500);
      eventData.location = sanitizeChatUxToken(meta.location, 'deck');
      break;
    case 'chat_tool_result':
      eventData.tool = wsPptEnum(meta.tool, CHAT_TOOL_KINDS, 'unknown');
      eventData.outcome = wsPptEnum(meta.outcome, CHAT_TOOL_OUTCOMES, 'unknown');
      eventData.changed_count = clampChatUxNumber(meta.changedCount, 500);
      eventData.edit_count = clampChatUxNumber(meta.editCount, 50);
      break;
    case 'canvas_opened':
    case 'canvas_saved':
      eventData.page_count = clampChatUxNumber(meta.pageCount ?? meta.page_count, 500);
      eventData.object_count = clampChatUxNumber(meta.objectCount ?? meta.object_count, 100_000);
      break;
    case 'artifact_saved':
    case 'artifact_downloaded':
      eventData.format = wsPptEnum(meta.format, WS_PPT_FORMATS, 'unknown');
      eventData.page_count = clampChatUxNumber(meta.pageCount ?? meta.page_count, 500);
      if (action === 'artifact_downloaded') {
        eventData.location = wsPptEnum(meta.location ?? meta.source, WS_PPT_DOWNLOAD_LOCATIONS, 'unknown');
      }
      break;
    case 'resume_edit':
      eventData.source = wsPptEnum(meta.source, WS_PPT_RESUME_SOURCES, 'unknown');
      break;
    case 'version_restored':
      eventData.version_index = clampChatUxNumber(meta.versionIndex ?? meta.version_index, 10_000);
      break;
    case 'regen_requested': {
      eventData.scope = wsPptEnum(meta.scope, WS_PPT_REGEN_SCOPES, 'unknown');
      if (meta.source) eventData.source = sanitizeChatUxToken(meta.source, 'unknown');
      if (meta.target) eventData.target = sanitizeChatUxToken(meta.target, 'unknown');
      // 쪽 단위 다시 만들기일 때만 몇 쪽인지 남긴다 (전체/문항 단위는 생략)
      const regenPageCount = meta.pageCount ?? meta.page_count;
      if (regenPageCount !== undefined && regenPageCount !== null) {
        eventData.page_count = clampChatUxNumber(regenPageCount, 500);
      }
      // 쪽 부분 수정: 지시문 원문은 보내지 않고 길이만 남긴다
      if (eventData.scope === 'region') {
        eventData.page_index = clampChatUxNumber(meta.pageIndex ?? meta.page_index, 500);
        eventData.has_mask = Boolean(meta.hasMask ?? meta.has_mask);
        eventData.instruction_len = clampChatUxNumber(meta.instructionLength ?? meta.instruction_len, 2000);
      }
      break;
    }
    case 'regen_diff':
      eventData.changed_count = clampChatUxNumber(meta.changedCount ?? meta.changed_count, 10_000);
      break;
    case 'free_design_started':
    case 'free_design_to_image':
      eventData.page_count = clampChatUxNumber(meta.slideCount, 500);
      break;
    case 'free_design_completed':
      eventData.page_count = clampChatUxNumber(meta.slideCount, 500);
      eventData.elapsed_ms = clampChatUxNumber((Number(meta.seconds) || 0) * 1000, 3_600_000);
      eventData.latency_bucket = getChatLatencyBucket(eventData.elapsed_ms);
      break;
    case 'free_design_failed':
      eventData.error_code = sanitizeChatUxToken(meta.code, 'unknown');
      break;
    case 'free_design_resume_wait':
    case 'free_design_resume_timeout':
      eventData.restyle = meta.restyle === true;
      break;
    case 'free_design_resumed':
      eventData.restyle = meta.restyle === true;
      eventData.elapsed_ms = clampChatUxNumber((Number(meta.seconds) || 0) * 1000, 3_600_000);
      eventData.latency_bucket = getChatLatencyBucket(eventData.elapsed_ms);
      break;
    case 'chat_action_guard':
      eventData.guard = wsPptEnum(meta.guard, CHAT_ACTION_GUARDS, 'unknown');
      eventData.from_action = sanitizeChatUxToken(meta.fromAction, 'none');
      break;
    case 'free_design_redesign':
      eventData.has_instruction = meta.hasInstruction === true;
      break;
    case 'free_design_chat_sync':
      eventData.scope = wsPptEnum(meta.scope, FREE_DESIGN_SYNC_SCOPES, 'unknown');
      eventData.changed_count = clampChatUxNumber(meta.count, 500);
      break;
    case 'free_design_export':
      eventData.format = wsPptEnum(meta.format, WS_PPT_FORMATS, 'unknown');
      break;
    case 'free_design_element_move':
    case 'free_design_element_delete':
    case 'free_design_element_undo':
      eventData.object_count = clampChatUxNumber(meta.count ?? 1, 1000);
      break;
    case 'pptx_auto_generated':
      eventData.page_count = clampChatUxNumber(meta.pageCount ?? meta.page_count, 500);
      eventData.reason = sanitizeChatUxToken(meta.reason, 'unknown');
      break;
    case 'hwpx_auto_generated':
      eventData.page_count = clampChatUxNumber(meta.pageCount ?? meta.page_count, 500);
      eventData.panel_opened = meta.panelOpened === true;
      eventData.reason = sanitizeChatUxToken(meta.reason, 'unknown');
      break;
    case 'image_slides_reordered':
    case 'image_slide_deleted':
      eventData.page_count = clampChatUxNumber(meta.pageCount ?? meta.page_count, 500);
      break;
    case 'hwpx_rerender_conflict_choice':
      eventData.choice = sanitizeChatUxToken(meta.choice, 'unknown');
      break;
    case 'image_generate_started':
      eventData.entry = sanitizeChatUxToken(meta.entry, 'unknown');
      eventData.reuse_design = meta.reuseDesign === true;
      break;
    case 'image_generate_finished': {
      eventData.entry = sanitizeChatUxToken(meta.entry, 'unknown');
      eventData.outcome = sanitizeChatUxToken(meta.outcome, 'unknown');
      eventData.page_count = clampChatUxNumber(meta.pageCount ?? meta.page_count, 500);
      const elapsedMs = clampChatUxNumber(meta.elapsedMs ?? meta.elapsed_ms, 3_600_000);
      eventData.elapsed_ms = elapsedMs;
      eventData.latency_bucket = getChatLatencyBucket(elapsedMs);
      break;
    }
    case 'image_request_resolved':
      eventData.entry = sanitizeChatUxToken(meta.entry, 'unknown');
      eventData.decision = sanitizeChatUxToken(meta.decision, 'unknown');
      eventData.image_stale = meta.imageStale === true;
      break;
    default:
      break;
  }

  try {
    window.gtag?.('event', eventType, { ...eventData, event_category: `${prefix}_overhaul` });
  } catch {
    // GA 실패는 기능 흐름을 막지 않는다.
  }
  sendEvent(eventType, eventData);
  return true;
};

/** 학습지 이벤트 → `ws_<action>` */
export const trackWorksheetEvent = (action, data = {}) => trackWsPptEvent('ws', 'worksheet', action, data);
/** PPT 이벤트 → `ppt_<action>` */
export const trackPptEvent = (action, data = {}) => trackWsPptEvent('ppt', 'ppt', action, data);

/**
 * 우리반 학급사진 이벤트 퍼널.
 * 프롬프트, 사진 URL, 파일명, 학급명 같은 원문은 절대 전송하지 않는다.
 */
export const trackClassroomBundleEvent = (action, metadata = {}) => {
  if (!CLASSROOM_BUNDLE_ACTIONS.has(action)) return false;

  const elapsedMs = clampChatUxNumber(metadata.elapsedMs, 3_600_000);
  const eventType = `classroom_bundle_${action}`;
  const eventData = {
    action,
    artifact_count: clampChatUxNumber(metadata.artifactCount, 20),
    concept_id: sanitizeChatUxToken(metadata.conceptId, 'unknown'),
    elapsed_ms: elapsedMs,
    entry: sanitizeChatUxToken(metadata.entry, 'unknown'),
    error_code: sanitizeChatUxToken(metadata.errorCode, 'none'),
    event_id: sanitizeChatUxToken(metadata.eventId, 'none'),
    is_anonymous: metadata.isAnonymous === true,
    is_pro: metadata.isPro === true,
    latency_bucket: getChatLatencyBucket(elapsedMs),
    purpose: sanitizeChatUxToken(metadata.purpose, 'unknown'),
    reference_attached: metadata.referenceAttached === true,
    share_id: sanitizeChatUxToken(metadata.shareId, 'none'),
    stage: sanitizeChatUxToken(metadata.stage, 'unknown'),
    target_category: sanitizeChatUxToken(metadata.targetCategory, 'none'),
    variant_id: sanitizeChatUxToken(metadata.variantId, 'control'),
    viewport_group: getChatViewportGroup(),
  };

  try {
    window.gtag?.('event', eventType, {
      ...eventData,
      event_category: 'classroom_bundle',
    });
    if (
      action === 'generation_completed'
      && eventData.event_id !== 'none'
      && typeof window.fbq === 'function'
    ) {
      window.fbq(
        'trackCustom',
        'ClassroomBundleResult',
        {
          content_category: eventData.concept_id,
          content_name: eventData.purpose,
        },
        { eventID: eventData.event_id }
      );
    }
  } catch {
    // GA 실패는 사용자 흐름을 막지 않는다.
  }
  sendEvent(eventType, eventData, { omitUserEmail: true });
  return true;
};

const buildClassCampaignEventData = (metadata = {}) => {
  const elapsedMs = clampChatUxNumber(metadata.elapsedMs, 3_600_000);
  return {
    action: metadata.action,
    angle: sanitizeChatUxToken(metadata.angle, 'none'),
    elapsed_ms: elapsedMs,
    entry: sanitizeChatUxToken(metadata.entry, 'unknown'),
    error_code: sanitizeChatUxToken(metadata.errorCode, 'none'),
    event_id: sanitizeChatUxToken(metadata.eventId, 'none'),
    is_anonymous: metadata.isAnonymous === true,
    is_pro: metadata.isPro === true,
    latency_bucket: getChatLatencyBucket(elapsedMs),
    share_id: sanitizeChatUxToken(metadata.shareId, 'none'),
    stage: sanitizeChatUxToken(metadata.stage, 'unknown'),
    target_category: sanitizeChatUxToken(metadata.targetCategory, 'none'),
    variant_id: sanitizeChatUxToken(metadata.variantId, 'control'),
    viewport_group: getChatViewportGroup(),
  };
};

const trackClassCampaignFamilyEvent = ({
  action,
  metadata = {},
  prefix,
  eventCategory,
  pixelEventName,
  pixelContentCategory,
  allowedActions = CLASS_CAMPAIGN_EVENT_ACTIONS,
}) => {
  if (!allowedActions.has(action)) return false;

  if (metadata.campaign && action === 'landing_viewed') {
    ensureDefaultCampaignUtm(metadata.campaign, {
      content: metadata.variantId,
    });
  }

  const eventType = `${prefix}${action}`;
  const eventData = buildClassCampaignEventData({ ...metadata, action });

  try {
    window.gtag?.('event', eventType, {
      ...eventData,
      event_category: eventCategory,
    });
    if (
      action === 'generation_completed'
      && eventData.event_id !== 'none'
      && typeof window.fbq === 'function'
    ) {
      window.fbq(
        'trackCustom',
        pixelEventName,
        {
          content_category: pixelContentCategory,
          content_name: eventData.variant_id,
        },
        { eventID: eventData.event_id }
      );
    }
  } catch {
    // GA 실패는 사용자 흐름을 막지 않는다.
  }
  sendEvent(eventType, eventData, { omitUserEmail: true });
  return true;
};

/**
 * 우리 반 활동지 이벤트 퍼널.
 * 우리 반 이야기, 반 이름, 이메일 같은 원문은 절대 전송하지 않는다.
 */
export const trackClassActivityEvent = (action, metadata = {}) => (
  trackClassCampaignFamilyEvent({
    action,
    metadata,
    prefix: 'class_activity_',
    eventCategory: 'class_activity',
    pixelEventName: 'ClassActivityResult',
    pixelContentCategory: 'class_activity',
  })
);

/**
 * 우리 반 교실 놀이 PPT 이벤트 퍼널.
 * 우리 반 이야기, 자유 주제, 이메일 같은 원문은 절대 전송하지 않는다.
 */
export const trackClassPlayEvent = (action, metadata = {}) => (
  trackClassCampaignFamilyEvent({
    action,
    metadata,
    prefix: 'class_play_',
    eventCategory: 'class_play',
    pixelEventName: 'ClassPlayResult',
    pixelContentCategory: 'class_play',
  })
);

const AI30_EVENT_ACTIONS = new Set([
  'landing_viewed',
  'cta_clicked',
  'signup_clicked',
  'school_level_selected',
  'share_clicked',
  'trial_applied',
  'trial_blocked',
  'first_task_started',
]);

const INAPP_BROWSER_ACTIONS = new Set([
  'login_blocked',
  'open_external_clicked',
  'email_continue_clicked',
  'banner_shown',
]);

export const trackInAppBrowserEvent = (action, metadata = {}) => {
  if (!INAPP_BROWSER_ACTIONS.has(action)) return false;
  const eventData = {
    browser: sanitizeChatUxToken(metadata.browser, 'unknown'),
    platform: sanitizeChatUxToken(metadata.platform, 'unknown'),
    entry: sanitizeChatUxToken(metadata.entry, 'unknown'),
  };
  try {
    window.gtag?.('event', `inapp_${action}`, { ...eventData, event_category: 'inapp_browser' });
  } catch {
    // GA 실패는 사용자 흐름을 막지 않는다.
  }
  sendEvent(`inapp_${action}`, eventData);
  return true;
};

const SCHOOL_PURCHASE_ACTIONS = new Set([
  'path_selected',
  'docs_zip_clicked',
  's2b_clicked',
  'docs_requested',
  'docs_request_failed',
  'office_note_copied',
  // 카드 결제(쿠폰 발급) 경로: 결제창 열기 전 주문 생성부터 — reason 은 http_<상태>·토스 오류 코드 토큰만
  'card_checkout_started',
  'card_checkout_failed',
  'card_checkout_cancelled',
]);

/**
 * 학교 구매(서류 받기 → 품의 → 학교장터·법인카드) 퍼널.
 * 학교명·이메일 같은 원문은 보내지 않는다. 서버의 document_requests가 신원 기록을 맡는다.
 */
export const trackSchoolPurchaseEvent = (action, metadata = {}) => {
  if (!SCHOOL_PURCHASE_ACTIONS.has(action)) return false;
  const eventType = `school_purchase_${action}`;
  const eventData = {
    entry: sanitizeChatUxToken(metadata.entry, 'unknown'),
    path: sanitizeChatUxToken(metadata.path, 'none'),
    plan: sanitizeChatUxToken(metadata.plan, 'none'),
    quantity: Number.isFinite(Number(metadata.quantity)) ? Number(metadata.quantity) : 0,
    reason: sanitizeChatUxToken(metadata.reason, 'none'),
    promo: sanitizeChatUxToken(metadata.promo, 'none'),
  };
  try {
    window.gtag?.('event', eventType, { ...eventData, event_category: 'school_purchase' });
  } catch {
    // GA 실패는 사용자 흐름을 막지 않는다.
  }
  sendEvent(eventType, eventData, { omitUserEmail: true });
  return true;
};

/**
 * 2학기 최신 유료 AI 30일 캠페인 퍼널.
 * 이메일, 학교명 같은 원문은 절대 전송하지 않는다. 학교급은 사전 정의된
 * 토큰(elementary/middle/high/other)만 보낸다.
 */
export const trackAi30Event = (action, metadata = {}) => (
  trackClassCampaignFamilyEvent({
    action,
    metadata: { campaign: 'b2g_school_adoption_2026q3', ...metadata },
    prefix: 'ai30_',
    eventCategory: 'ai30_campaign',
    pixelEventName: 'Ai30Result',
    pixelContentCategory: 'ai30_campaign',
    allowedActions: AI30_EVENT_ACTIONS,
  })
);

const FESTA_EVENT_ACTIONS = new Set([
  'landing_viewed',
  'cta_clicked',
  'signup_clicked',
  'trial_applied',
  'trial_blocked',
  'budget_cta_clicked',
  'budget_submitted',
  'budget_modal_opened',
  'budget_modal_closed',
  'preview_tab_clicked',
]);

/**
 * 연말 페스타(쿨메신저 12월) 캠페인 퍼널 (/events/year-end-festa).
 * 학교명, 이메일, 상담 내용 같은 원문은 절대 전송하지 않는다.
 */
export const trackFestaEvent = (action, metadata = {}) => (
  trackClassCampaignFamilyEvent({
    action,
    metadata: { campaign: 'b2g_school_adoption_2026q4', ...metadata },
    prefix: 'festa_',
    eventCategory: 'festa_campaign',
    pixelEventName: 'FestaResult',
    pixelContentCategory: 'festa_campaign',
    allowedActions: FESTA_EVENT_ACTIONS,
  })
);

const GROUP50_EVENT_ACTIONS = new Set([
  'landing_viewed',
  'cta_clicked',
  'quote_clicked',
  'quote_requested',
  'crosslink_clicked',
  'share_clicked',
  // 사이트 전역 공지 팝업 (config/announcements.js)
  'popup_viewed',
  'popup_cta_clicked',
  'popup_dismissed',
]);

/**
 * 10명 이상 단체 구매 50% 캠페인 퍼널 (/events/group-50).
 * 학교명, 이메일 같은 원문은 절대 전송하지 않는다.
 */
export const trackGroup50Event = (action, metadata = {}) => (
  trackClassCampaignFamilyEvent({
    action,
    metadata: { campaign: 'group50_2026q3', ...metadata },
    prefix: 'group50_',
    eventCategory: 'group50_campaign',
    pixelEventName: 'Group50Result',
    pixelContentCategory: 'group50_campaign',
    allowedActions: GROUP50_EVENT_ACTIONS,
  })
);

const PRO50_EVENT_ACTIONS = new Set([
  'landing_viewed',
  // 랜딩 신청 폼 (EventPreRegisterForm onTrack)
  'apply_clicked',
  'preregister_submitted',
  'preregister_error',
  'preregister_invalid',
  'cta_clicked',
  'bulk_clicked',
  'quote_clicked',
  'quote_requested',
  'crosslink_clicked',
  'share_clicked',
  'redirected_from_group50',
  // 사이트 전역 공지 팝업 (config/announcements.js)
  'popup_viewed',
  'popup_cta_clicked',
  'popup_dismissed',
]);

/**
 * 10월 PRO 최대 50% 캠페인 퍼널 (/events/pro-50, 인원 제한 없음).
 * 학교명, 이메일 같은 원문은 절대 전송하지 않는다.
 */
export const trackPro50Event = (action, metadata = {}) => (
  trackClassCampaignFamilyEvent({
    action,
    metadata: { campaign: 'pro50_2026q4', ...metadata },
    prefix: 'pro50_',
    eventCategory: 'pro50_campaign',
    pixelEventName: 'Pro50Result',
    pixelContentCategory: 'pro50_campaign',
    allowedActions: PRO50_EVENT_ACTIONS,
  })
);

const PRO_AI_EVENT_ACTIONS = new Set([
  'landing_viewed',
  'section_viewed',
  'cta_click',
  'faq_open',
  'price_toggle',
  'model_selected',
  'share_clicked',
  'preregister_view',
  'preregister_submitted',
  'preregister_error',
  'preregister_invalid',
  'welcome_click',
]);

/**
 * 왓퀴즈 PRO 최신 AI 3개 올인원 랜딩 퍼널 (/events/pro-ai).
 * 학교명, 이메일 같은 원문은 절대 전송하지 않는다.
 * cta_click meta.cta: individual_pricing | group_quote | group_buy | sticky | hero
 */
export const trackProAiEvent = (action, metadata = {}) => (
  trackClassCampaignFamilyEvent({
    action,
    metadata: {
      campaign: 'pro_ai_2026q3',
      stage: metadata.cta || metadata.stage,
      ...metadata,
    },
    prefix: 'proai_',
    eventCategory: 'proai_campaign',
    pixelEventName: 'ProAiResult',
    pixelContentCategory: 'proai_campaign',
    allowedActions: PRO_AI_EVENT_ACTIONS,
  })
);

const SCORING_EVENT_ACTIONS = new Set([
  'landing_viewed',
  'section_viewed',
  'cta_click',
  'faq_open',
  'demo_step',
  'share_clicked',
  'preregister_view',
  'preregister_submitted',
  'preregister_error',
  'preregister_invalid',
  'welcome_click',
]);

/**
 * AI 채점 광고 랜딩 퍼널 (/events/scoring).
 * 학생 이름, 답안 원문 같은 값은 절대 전송하지 않는다.
 * cta/section/step 은 stage 토큰으로만 남긴다.
 */
export const trackScoringLpEvent = (action, metadata = {}) => (
  trackClassCampaignFamilyEvent({
    action,
    metadata: {
      campaign: 'scoring_2026q3',
      ...metadata,
      stage: metadata.cta || metadata.section || metadata.step || metadata.stage,
    },
    prefix: 'scoring_lp_',
    eventCategory: 'scoring_lp',
    pixelEventName: 'ScoringLpResult',
    pixelContentCategory: 'scoring_lp',
    allowedActions: SCORING_EVENT_ACTIONS,
  })
);

const HWP_DOC_EVENT_ACTIONS = new Set([
  'landing_viewed',
  'preset_selected',
  'generation_started',
  'generation_completed',
  'preview_opened',
  // rhwp 미리보기 진단(퍼널 스테이지가 아니라 계측 전용 — analytics.py 퍼널 순서에는 넣지 않는다):
  //   preview_slow  = 12초 안에 열리지 않아 느림 안내를 얹음(뷰어 유지)
  //   preview_failed = 45초 초과 또는 뷰어 명시 실패 → 폴백 UI
  'preview_slow',
  'preview_failed',
  'share_clicked',
  'shared_result_viewed',
  'download_clicked',
  'account_gate',
  'signup_clicked',
  'registration_completed',
  'trial_started',
  'chat_handoff_clicked',
  // 핸드오프로 시작한 /chat 대화의 첫 응답 완료 = "이벤트 → 제품 사용" 전환
  'chat_first_generation',
  'crosslink_clicked',
  // 체험 소진 게이트의 PRO 이동 버튼
  'pricing_clicked',
  // 카카오톡·인스타·페북 인앱 웹뷰 (랜딩 1회 감지 / 외부 브라우저 열기 클릭)
  'inapp_detected',
  'inapp_open_external_clicked',
  'discount_bubble_impression',
  'payment_page_view',
  'payment_complete',
]);

/**
 * 한글 파일 이벤트 랜딩 퍼널 (/events/hwp-exam|hwp-notice|hwp-record).
 * 문서 원문·이메일은 절대 전송하지 않는다. type은 exam|notice|record만.
 * generation_completed는 event_id가 있을 때만 HwpDocResult Pixel을 보내 서버 CAPI와 dedup한다.
 * Lead Pixel은 이 퍼널에서 발화하지 않는다.
 * 허용 메타: destination(chat|saenggibu), doc_id(hwpdoc_… 토큰), browser(인앱 앱 이름),
 * 미리보기 진단(preview_*): stage(뷰어 단계 토큰), reason(에러 토큰), wasm_ms·raw_bytes_ms·parse_ms·retry_count(숫자).
 */
const HWP_DOC_PREVIEW_STAGE_MS_KEYS = [
  ['wasmMs', 'wasm_ms'],
  ['rawBytesMs', 'raw_bytes_ms'],
  ['parseMs', 'parse_ms'],
];

export const trackHwpDocEvent = (action, metadata = {}) => {
  if (!HWP_DOC_EVENT_ACTIONS.has(action)) return false;

  const docType = sanitizeChatUxToken(
    metadata.docType || metadata.type || metadata.variantId,
    'exam',
  );

  if (action === 'landing_viewed') {
    ensureDefaultCampaignUtm(metadata.campaign || 'hwp_doc_2026q3', {
      content: metadata.utm_content || metadata.variantId || docType,
    });
  }

  const loadMsRaw = metadata.load_ms ?? metadata.loadMs;
  const eventType = `hwp_doc_${action}`;
  const eventData = {
    ...buildClassCampaignEventData({
      ...metadata,
      action,
      variantId: docType,
    }),
    doc_type: docType,
    ...(Number.isFinite(Number(loadMsRaw)) && {
      load_ms: Math.max(0, Math.round(Number(loadMsRaw))),
    }),
    ...(metadata.field && {
      field: sanitizeChatUxToken(metadata.field, 'unknown'),
    }),
    ...(metadata.destination && {
      destination: sanitizeChatUxToken(metadata.destination, 'unknown'),
    }),
    ...(metadata.docId && {
      doc_id: sanitizeChatUxToken(metadata.docId, 'none'),
    }),
    ...(metadata.browser && {
      browser: sanitizeChatUxToken(metadata.browser, 'unknown'),
    }),
    // 미리보기 단계 계측(preview_opened/slow/failed). 값이 숫자일 때만 실어 다른 액션 페이로드는 그대로 둔다.
    ...Object.fromEntries(
      HWP_DOC_PREVIEW_STAGE_MS_KEYS
        .filter(([source]) => Number.isFinite(Number(metadata[source])) && metadata[source] !== null)
        .map(([source, target]) => [target, clampChatUxNumber(metadata[source], 600_000)]),
    ),
    ...(Number.isFinite(Number(metadata.retryCount)) && metadata.retryCount !== null && {
      retry_count: clampChatUxNumber(metadata.retryCount, 99),
    }),
    ...(metadata.reason && {
      reason: sanitizeChatUxToken(metadata.reason, 'unknown'),
    }),
  };

  try {
    window.gtag?.('event', eventType, {
      ...eventData,
      event_category: 'hwp_doc',
    });
    if (
      typeof window.fbq === 'function'
      && action === 'generation_completed'
      && eventData.event_id !== 'none'
    ) {
      window.fbq(
        'trackCustom',
        'HwpDocResult',
        { content_name: docType },
        { eventID: eventData.event_id },
      );
    }
  } catch {
    // GA/Pixel 실패는 사용자 흐름을 막지 않는다.
  }
  sendEvent(eventType, eventData, { omitUserEmail: true });
  return true;
};

const createPixelEventId = () => {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch { /* crypto 미지원 브라우저 */ }
  return `evt_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

/**
 * GROUP50 결제 페이지 도착 (`/bulk-purchase?promo=group50` 로드 시 1회).
 * 브라우저 fbq(Group50CheckoutView)와 서버 CAPI(routes/analytics.py)가 같은
 * event_id를 공유해 Meta가 중복 제거한다. 1회성 보장은 호출부(BulkPurchasePage)의
 * ref 가드가 담당하고, promo 없는 일반 진입에서는 호출 자체를 하지 않는다.
 * 학교명·이메일 같은 원문은 절대 보내지 않는다 — plan/인원/예상금액만.
 */
export const trackGroup50CheckoutView = (metadata = {}) => {
  const eventId = sanitizeChatUxToken(metadata.eventId, '') || createPixelEventId();
  const plan = sanitizeChatUxToken(metadata.plan, 'unknown');
  const quantity = clampChatUxNumber(metadata.quantity, 500);
  const value = clampChatUxNumber(metadata.value, 100_000_000);

  // 결제 페이지는 이미 있는 UTM만 따른다. 내부 진입에 event_landing을 심지 않는다.
  const eventData = {
    action: 'checkout_viewed',
    campaign: 'group50_2026q3',
    currency: 'KRW',
    event_id: eventId,
    // 서버 CAPI(Group50CheckoutView)가 읽는 필드들
    group50_event_id: eventId,
    ...getMetaMatchParams(),
    event_source_url: `${window.location.origin}${window.location.pathname}${window.location.search}`,
    num_items: quantity,
    plan,
    value,
    viewport_group: getChatViewportGroup(),
  };

  try {
    window.gtag?.('event', 'group50_checkout_viewed', {
      ...eventData,
      event_category: 'group50_campaign',
    });
    if (typeof window.fbq === 'function') {
      window.fbq(
        'trackCustom',
        'Group50CheckoutView',
        {
          content_category: 'group50_campaign',
          content_name: plan,
          currency: 'KRW',
          num_items: quantity,
          value,
        },
        { eventID: eventId },
      );
    }
  } catch {
    // 트래킹 실패는 결제 흐름을 막지 않는다.
  }
  sendEvent('group50_checkout_viewed', eventData, { omitUserEmail: true });
  return true;
};

/**
 * PRO50 결제 페이지 도착 — 개인(/subscription/pro)·단체(/bulk-purchase) 모두.
 * 이벤트가가 실제로 적용된 화면에서만 1회 (호출부 ref 가드).
 * 브라우저 fbq(Pro50CheckoutView)와 서버 CAPI가 같은 event_id로 중복 제거된다.
 */
export const trackPro50CheckoutView = (metadata = {}) => {
  const eventId = sanitizeChatUxToken(metadata.eventId, '') || createPixelEventId();
  const plan = sanitizeChatUxToken(metadata.plan, 'unknown');
  const quantity = clampChatUxNumber(metadata.quantity, 500);
  const value = clampChatUxNumber(metadata.value, 100_000_000);
  const checkoutType = metadata.checkoutType === 'bulk' ? 'bulk' : 'personal';

  const eventData = {
    action: 'checkout_viewed',
    campaign: 'pro50_2026q4',
    checkout_type: checkoutType,
    currency: 'KRW',
    event_id: eventId,
    pro50_event_id: eventId,
    ...getMetaMatchParams(),
    event_source_url: `${window.location.origin}${window.location.pathname}${window.location.search}`,
    num_items: quantity,
    plan,
    value,
    viewport_group: getChatViewportGroup(),
  };

  try {
    window.gtag?.('event', 'pro50_checkout_viewed', {
      ...eventData,
      event_category: 'pro50_campaign',
    });
    if (typeof window.fbq === 'function') {
      window.fbq(
        'trackCustom',
        'Pro50CheckoutView',
        {
          content_category: 'pro50_campaign',
          content_name: plan,
          checkout_type: checkoutType,
          currency: 'KRW',
          num_items: quantity,
          value,
        },
        { eventID: eventId },
      );
    }
  } catch {
    // 트래킹 실패는 결제 흐름을 막지 않는다.
  }
  sendEvent('pro50_checkout_viewed', eventData, { omitUserEmail: true });
  return true;
};

// AI30 캠페인 쿠폰 — pro_30day_trial 그룹 중 캠페인 코드만 (범용 PRO30/FREE30 제외)
export const AI30_TRIAL_COUPON_CODES = ['AI30', 'TEACHER30'];

/**
 * Ai30TrialApplied dedup event_id. 서버(event_campaign_capi.build_ai30_trial_event_id)와
 * 같은 공식이라 별도 전달 없이도 Pixel/CAPI가 중복 제거되고, 사용자당 1회로 수렴한다.
 */
export const buildAi30TrialAppliedEventId = (uid) => {
  const token = String(uid || '').trim();
  if (!/^[A-Za-z0-9._-]{1,70}$/.test(token)) return null;
  return `ai30trial-${token}`;
};

/**
 * AI30 캠페인 쿠폰(AI30/TEACHER30)이 실제로 적용 성공한 순간의 브라우저 픽셀.
 * 호출부(couponService.claimCoupon)가 신규 적용(already_claimed=false)일 때만 부른다.
 * 내부 퍼널의 ai30_trial_applied(Ai30ArrivalTracker)와 별개 — 여기서는 Meta 픽셀만 쏜다.
 */
export const trackAi30TrialAppliedPixel = ({ couponCode, eventId }) => {
  const safeEventId = sanitizeChatUxToken(eventId, 'none');
  if (safeEventId === 'none') return false;
  try {
    if (typeof window.fbq !== 'function') return false;
    window.fbq(
      'trackCustom',
      'Ai30TrialApplied',
      {
        campaign: 'b2g_school_adoption_2026q3',
        content_category: 'ai30_campaign',
        content_name: sanitizeChatUxToken(couponCode, 'AI30'),
        coupon_code: sanitizeChatUxToken(couponCode, 'AI30'),
      },
      { eventID: safeEventId },
    );
    return true;
  } catch {
    return false;
  }
};

export const trackInviteShare = (
  surface,
  method,
  referralAttached = false,
) => sendEvent('invite_share_opened', {
  surface: String(surface || 'unknown').slice(0, 40),
  method: String(method || 'unknown').slice(0, 30),
  referral_attached: referralAttached === true,
});

/**
 * AI 맞춤 설문 퍼널. 학교명·과목명 등 원문 답변은 analytics에 복제하지 않고
 * 시작/완료/닫기와 비식별 개수만 기록한다.
 */
export const trackPersonalizationSurvey = (action, metadata = {}) => {
  sendEvent('personalization_survey', {
    action,
    survey_version: 1,
    step: Number(metadata.step || 0),
    task_count: Number(metadata.taskCount || 0),
    school_name_provided: Boolean(metadata.schoolNameProvided),
  });
};

// ============================================================================
// RISE 실증사업 -- 10개 카테고리 기능 추적
// ============================================================================

const RISE_SAVINGS_PER_USE = {
  saenggibu_generate: 17,
  saenggibu_inspect: 4,
  document_generate: 20,
  parent_comm: 15,
  counseling: 10,
  ai_chat: 10,
  rubric_plan: 40,
  worksheet: 30,
  ppt: 30,
  scoring: 5,
  image_generate: 20,
};

/**
 * RISE 실증 기능 사용 추적
 * @param {string} category - 10개 카테고리 중 하나 (기능 카테고리, metadata로 덮어쓰지 않음)
 * @param {string} action - 'generate'(기본), 'copy', 'save', 'download'
 * @param {object} metadata - 추가 데이터 (count, school_level 등).
 *   metadata.category는 기능 카테고리를 덮어쓰지 않고 saenggibu_category로 옮긴다.
 */
export const trackRiseFeatureAction = (category, action = 'generate', metadata = {}) => {
  const rawMeta = metadata && typeof metadata === 'object' ? metadata : {};
  const {
    category: metadataCategory,
    saenggibu_category: explicitSaenggibuCategory,
    ...restMetadata
  } = rawMeta;
  const saenggibuCategory = explicitSaenggibuCategory ?? metadataCategory;

  sendEvent('rise_feature_action', {
    ...restMetadata,
    category,
    action,
    savings_minutes: RISE_SAVINGS_PER_USE[category] || 0,
    ...(saenggibuCategory != null && saenggibuCategory !== ''
      ? { saenggibu_category: saenggibuCategory }
      : {}),
  });
};

const SAENGGIBU_UX_ACTIONS = Object.freeze([
  'saenggibu_copy',
  'saenggibu_regenerate_preset',
  'saenggibu_regenerate_custom',
  'saenggibu_manual_edit',
  'saenggibu_style_entry',
  'saenggibu_style_saved',
  'saenggibu_example_saved',
  'saenggibu_suggestion_shown',
  'saenggibu_suggestion_accepted',
  'saenggibu_suggestion_dismissed',
  'saenggibu_library_pick',
  // 생성 후 이어 쓰기 흐름 (2026-09): 설정 유지형 후속 생성과 초기화를 구분해 측정한다
  'saenggibu_batch_next_class',        // 다중 결과 → 다음 학급 만들기(설정 유지)
  'saenggibu_batch_reset_all',         // 다중 결과 → 설정도 초기화하고 새로 시작
  'saenggibu_batch_back_to_input',     // 다중 결과 → 입력으로 돌아가기
  'saenggibu_batch_category_switch_after_upload', // 다중 1단계에서 파일 올린 뒤 종류 변경
  'saenggibu_class_evidence_reviewed', // 학급 관리에서 가져온 학생 글: 생성 시작 때 고친·뺀 학생 수(본문 없음)
  'saenggibu_mode_switch',             // 단일↔다중 토글
  'batch_scan_range_confirmed',        // 스캔 범위 확인 (화이트리스트 누락으로 버려지던 이벤트)
  'saenggibu_batch_export',            // 다중 결과 엑셀 저장 = 반 완료 신호
  // 아래 셋은 화이트리스트 누락으로 운영에서 버려지던 이벤트
  'saenggibu_class_check_make_different',
  'saenggibu_style_quick_applied',
  'saenggibu_first_day_unlock_seen',
  // 다시 만들기 요청 목록 관리 (2026-10)
  'saenggibu_regen_chain_edit',
  'saenggibu_regen_chain_delete',
  'saenggibu_regen_chain_undo',
  'saenggibu_regen_chain_clear',
  'saenggibu_regen_chain_override_sent', // 고친 목록으로 실제 다시 만들기를 보냄
  'saenggibu_next_student',              // 단일: 설정은 두고 다음 학생 입력으로
  'saenggibu_roster_link',               // 단일 기록을 명단 학생에 담기 stage=before|after|copy|unlink(+_failed)
  // 반 점검(같은 문장·거의 같은 문장) (2026-10)
  'saenggibu_class_check_shown',         // 점검 결과 표시(학생 수·겹침 수·심각도)
  'saenggibu_class_check_fix_all',       // 겹치지 않게 N명 고치기 stage=start|done|undo
  'saenggibu_prior_semester',            // 1학기에 쓴 글과 겹치지 않게 stage=shown|on|off (찾은 학생 수)
  'saenggibu_similar_inline_open',       // 결과 카드 아래에서 비슷한 문장 열기 (탭 이동 없이)
  'saenggibu_batch_merge_same_student',  // 다중 확인: 같은 이름 학생 stage=shown|merge|keep|undo (그룹 수·늘어난 항목 수)
  'saenggibu_batch_eval_plan_pick',      // 다중 평가계획서: 평가 고르기 stage=shown|single|all|change (평가 수)
  'saenggibu_multi_student_file',        // 단일: 여러 학생 파일 stage=shown|pick|to_batch (찾은 학생 수·고른 수)
  'saenggibu_trait_chip',                // 행동발달 특성 칩 stage=add|remove|expand|search|scene_open|scene_add|scene_close|sheet_open (칩 id만)
  // 근거 보기 (2026-11): surface=single|batch|multi_class|history, entry=summary|sentence
  'saenggibu_evidence_open',
  'saenggibu_evidence_sentence_click',   // stage=copy|reword|combine|summary|direction|edited
  'saenggibu_evidence_revise',           // stage=reword|shorter|request|manual|remove|insert
  'saenggibu_evidence_close',
]);
// 숫자 지표(개수·인원). 이름·본문은 받지 않는다.
const SAENGGIBU_UX_COUNT_KEYS = [
  'versions_count', 'content_bytes', 'target_bytes', 'student_count', 'over_count', 'revised_count',
  'item_count', 'same_count', 'near_count', 'flagged_count', 'common_count', 'max_people',
  'severe_count', 'area_count', 'chain_count', 'note_count', 'found_count', 'semester_overlap_count',
  'opening_count', 'slop_count', 'similar_count', 'removed_count', 'elapsed_ms',
];
// 정해진 짧은 영문 토큰만 남기는 필드(위치·단계·이유)
const SAENGGIBU_UX_TOKEN_KEYS = ['surface', 'reasons', 'stage', 'entry', 'scope', 'level', 'file_kind'];
const SAENGGIBU_RECORD_ID_PATTERN = /^[A-Za-z0-9_-]{6,64}$/;
export const SAENGGIBU_TRAIT_CHIP_ID_PATTERN = /^bt_[a-z0-9_]{2,48}$/;
const STYLE_EXAMPLE_SOURCES = new Set(['manual', 'from_output', 'from_edit']);
const SAENGGIBU_SUGGESTION_TYPES = new Set([
  'style_rule_from_regen',
  'save_output_as_example',
  'apply_saved_style',
  'batch_instead_of_single',
  'premium_gap_style',
  'reuse_eval_plan',
]);
const SAENGGIBU_UX_ACTION_SET = new Set(SAENGGIBU_UX_ACTIONS);
const SAENGGIBU_UX_CATEGORIES = new Set([
  '행동발달',
  '교과',
  '교과_고등',
  '창체',
  '동아리',
  '진로',
  '비슷한문장',
  'global',
]);

const sanitizeSaenggibuUxCategory = (value) => {
  const token = String(value || '').trim();
  return SAENGGIBU_UX_CATEGORIES.has(token) ? token : 'unknown';
};

const sanitizeSaenggibuPresetId = (value) => {
  const token = String(value || '')
    .trim()
    .slice(0, 60)
    .replace(/[^a-zA-Z0-9._-]/g, '_');
  return token || 'unknown';
};

const sanitizeSaenggibuSuggestionType = (value) => {
  const token = String(value || '').trim();
  return SAENGGIBU_SUGGESTION_TYPES.has(token) ? token : 'unknown';
};

/**
 * 생기부 UX 이벤트. 학생 원문·이름 등 PII는 받아도 버리고,
 * 종류/배치여부/버전 인덱스/프리셋 id만 남긴다.
 * @returns {boolean} 화이트리스트에 있는 action만 true
 */
export const trackSaenggibuUxEvent = (action, metadata = {}) => {
  if (!SAENGGIBU_UX_ACTION_SET.has(action)) return false;

  const meta = metadata && typeof metadata === 'object' ? metadata : {};
  const eventData = {
    action,
    saenggibu_category: sanitizeSaenggibuUxCategory(
      meta.saenggibu_category ?? meta.category
    ),
    is_batch: meta.is_batch === true,
  };

  if (meta.version_index !== undefined && meta.version_index !== null) {
    eventData.version_index = clampChatUxNumber(meta.version_index, 500);
  }
  // 객관 행동 로그: 어떤 기록의 몇 번째 버전을 얼마 분량으로 가져갔는지(본문은 보내지 않는다)
  const recordId = String(meta.record_id || '').trim();
  if (SAENGGIBU_RECORD_ID_PATTERN.test(recordId)) {
    eventData.record_id = recordId;
  }
  SAENGGIBU_UX_COUNT_KEYS.forEach((key) => {
    if (meta[key] !== undefined && meta[key] !== null && meta[key] !== '') {
      eventData[key] = clampChatUxNumber(meta[key], key === 'elapsed_ms' ? 3_600_000 : 100000);
    }
  });
  SAENGGIBU_UX_TOKEN_KEYS.forEach((key) => {
    if (meta[key] !== undefined && meta[key] !== null && meta[key] !== '') {
      eventData[key] = sanitizeChatUxToken(meta[key]);
    }
  });
  if (typeof meta.within_band === 'boolean') {
    eventData.within_band = meta.within_band;
  }
  if (action === 'saenggibu_regenerate_preset') {
    eventData.preset_id = sanitizeSaenggibuPresetId(meta.preset_id);
  }
  if (action === 'saenggibu_example_saved') {
    const source = String(meta.source || '').trim();
    eventData.source = STYLE_EXAMPLE_SOURCES.has(source) ? source : 'unknown';
  }
  if (
    action === 'saenggibu_suggestion_shown'
    || action === 'saenggibu_suggestion_accepted'
    || action === 'saenggibu_suggestion_dismissed'
  ) {
    eventData.suggestion_type = sanitizeSaenggibuSuggestionType(meta.suggestion_type);
  }
  if (action === 'saenggibu_trait_chip' && meta.chip_id !== undefined && meta.chip_id !== null) {
    // 라이브러리 칩 id만 남긴다. 교사가 직접 만든 칩 글자는 학생 정보가 섞일 수 있어 custom으로만 센다.
    const chipId = String(meta.chip_id).trim();
    eventData.chip_id = SAENGGIBU_TRAIT_CHIP_ID_PATTERN.test(chipId) ? chipId : 'custom';
  }
  if (action === 'saenggibu_library_pick') {
    // 자료 제목·파일명은 받지 않는다. 계획서류 여부만 남긴다.
    eventData.plan_like = meta.plan_like === true;
  }
  if (action === 'saenggibu_batch_category_switch_after_upload') {
    // 종류 사고(교과→행동발달 등) 재측정용: 어디서 어디로 바꿨는지와 학생 수만 남긴다
    eventData.from_category = sanitizeSaenggibuUxCategory(meta.from_category);
    eventData.student_count = clampChatUxNumber(meta.student_count, 500);
  }
  if (action === 'saenggibu_mode_switch') {
    const modeOf = (value) => (value === 'quick' || value === 'batch' ? value : 'unknown');
    eventData.from_mode = modeOf(meta.from_mode);
    eventData.to_mode = modeOf(meta.to_mode);
  }

  try {
    window.gtag?.('event', action, {
      ...eventData,
      event_category: 'saenggibu_ux',
    });
  } catch {
    // GA 실패는 생기부 흐름을 막지 않는다.
  }
  sendEvent(action, eventData);
  return true;
};

export { SAENGGIBU_UX_ACTIONS, CHAT_UX_ACTIONS };

export { RISE_SAVINGS_PER_USE };

// ============================================================================
// 채점(/scoring)·평가계획서(/assessment-plan) 단계별 퍼널 계측
//   기존에는 rise_feature_action(generate) 1개만 남아 "입력 → 실행 → 저장" 중 어디서
//   빠지는지 볼 수 없었다. 원문(기준서·답안·계획서·파일명)은 절대 보내지 않고
//   단계 토큰·개수·경과시간만 남긴다. 이벤트명은 scoring_ux_<action> / assessment_ux_<action>.
// ============================================================================

export const SCORING_UX_ACTIONS = Object.freeze([
  'rubric_ready',        // 기준서 입력이 처음 채워짐 (source: text|file|example)
  'answer_ready',        // 답안 입력이 처음 채워짐 (input_kind)
  'calibration_changed', // 채점 설정 변경 (field)
  'generate_started',
  'generate_completed',  // elapsed_ms·student_count
  'generate_failed',
  'rescore',             // 재채점 (preset)
  'save_completed',
  'export',              // format: xlsx|pdf|saenggibu(생기부로 이어가기)
  'review_open',         // 검토창 열기 (student_count, has_record)
  'review_close',        // 검토창 닫기 요약 (이동 방법별 횟수, 제출물 표시 방식별 학생 수)
]);
const SCORING_UX_ACTION_SET = new Set(SCORING_UX_ACTIONS);
const SCORING_INPUT_KINDS = new Set(['text', 'files', 'student_files', 'scan_pdf', 'single_file', 'room', 'unknown']);
const SCORING_MODES = new Set(['single', 'multi_question_single', 'batch', 'batch_split', 'scan_pdf', 'room', 'unknown']);
const SCORING_CALIBRATION_FIELDS = new Set(['school_level', 'strictness', 'distribution_target', 'custom_instruction', 'unknown']);
const SCORING_EXPORT_FORMATS = new Set(['xlsx', 'pdf', 'saenggibu', 'unknown']);
const SCORING_RUBRIC_SOURCES = new Set(['text', 'file', 'example', 'library', 'unknown']);

export const ASSESSMENT_UX_ACTIONS = Object.freeze([
  'type_selected',       // 계획서 종류 변경 (plan_type)
  'input_ready',         // 재료 입력이 처음 채워짐 (source: text|file|example)
  'subjects_detected',   // 여러 항목 감지 (item_count)
  'subject_choice',      // 시트 선택 (choice: all|single|multi, item_count)
  'generate_started',
  'generate_completed',  // elapsed_ms·has_hwp
  'generate_failed',
  'refine',              // scope: all|item
  'save_completed',
  'copy',
  'hwp_download',        // 한글 편집기에서 받기 (format: hwp|hwpx|pdf, ok)
  'hwp_work_save',       // 한글 편집기 작업 저장 (ok, reason)
  'hwp_skip_edits',      // 작업 저장 실패 뒤 직접 수정 없이 진행 (target: save|refine)
]);
const ASSESSMENT_UX_ACTION_SET = new Set(ASSESSMENT_UX_ACTIONS);
const ASSESSMENT_HWP_FORMATS = new Set(['hwp', 'hwpx', 'pdf', 'unknown']);
const ASSESSMENT_HWP_SKIP_TARGETS = new Set(['save', 'refine', 'unknown']);
const ASSESSMENT_PLAN_TYPES = new Set(['assessment', 'lesson_plan', 'project', 'essay_eval', 'inquiry', 'autonomous', 'unknown']);
const ASSESSMENT_SOURCES = new Set(['text', 'file', 'example', 'unknown', 'library', 'standards']);
const ASSESSMENT_CHOICES = new Set(['all', 'single', 'multi', 'unknown']);
const ASSESSMENT_MODES = new Set(['basic', 'detailed', 'unknown']);
const ASSESSMENT_REFINE_SCOPES = new Set(['all', 'item', 'unknown']);

const featureUxEnum = (value, allowed, fallback = 'unknown') => {
  const token = String(value || '').trim();
  return allowed.has(token) ? token : fallback;
};

const buildFeatureUxBase = (action, meta) => {
  const elapsedMs = clampChatUxNumber(meta.elapsedMs ?? meta.elapsed_ms, 3_600_000);
  const sinceOpenMs = clampChatUxNumber(meta.sinceOpenMs ?? meta.since_open_ms, 86_400_000);
  return {
    action,
    is_anonymous: meta.isAnonymous === true,
    is_pro: meta.isPro === true,
    since_open_ms: sinceOpenMs,             // 페이지 진입 후 경과 (첫 실행까지 시간 분석용)
    elapsed_ms: elapsedMs,
    latency_bucket: getChatLatencyBucket(elapsedMs),
    viewport_group: getChatViewportGroup(),
  };
};

const sendFeatureUxEvent = (prefix, category, eventData) => {
  const eventType = `${prefix}_${eventData.action}`;
  try {
    window.gtag?.('event', eventType, { ...eventData, event_category: category });
  } catch {
    // GA 실패는 기능 흐름을 막지 않는다.
  }
  sendEvent(eventType, eventData, { omitUserEmail: true });
  return true;
};

/**
 * 채점 페이지 단계 이벤트 → `scoring_ux_<action>`.
 * @returns {boolean} 화이트리스트에 없는 action이면 false
 */
export const trackScoringUxEvent = (action, metadata = {}) => {
  if (!SCORING_UX_ACTION_SET.has(action)) return false;
  const meta = metadata && typeof metadata === 'object' ? metadata : {};
  const eventData = {
    ...buildFeatureUxBase(action, meta),
    mode: featureUxEnum(meta.mode, SCORING_MODES),
  };
  switch (action) {
    case 'rubric_ready':
      eventData.source = featureUxEnum(meta.source, SCORING_RUBRIC_SOURCES);
      break;
    case 'answer_ready':
      eventData.input_kind = featureUxEnum(meta.inputKind ?? meta.input_kind, SCORING_INPUT_KINDS);
      eventData.student_count = clampChatUxNumber(meta.studentCount ?? meta.student_count, 1000);
      break;
    case 'calibration_changed':
      eventData.field = featureUxEnum(meta.field, SCORING_CALIBRATION_FIELDS);
      break;
    case 'generate_completed':
    case 'generate_started':
      eventData.student_count = clampChatUxNumber(meta.studentCount ?? meta.student_count, 1000);
      break;
    case 'generate_failed':
      eventData.error_code = sanitizeChatUxToken(meta.errorCode ?? meta.error_code, 'unknown');
      break;
    case 'rescore':
      eventData.preset = sanitizeChatUxToken(meta.preset, 'custom');
      break;
    case 'review_open':
      eventData.student_count = clampChatUxNumber(meta.studentCount ?? meta.student_count, 1000);
      eventData.has_record = meta.hasRecord === true;
      break;
    case 'review_close':
      eventData.student_count = clampChatUxNumber(meta.studentCount ?? meta.student_count, 1000);
      eventData.has_record = meta.hasRecord === true;
      [
        ['viewedCount', 'viewed_count'],
        ['navButtonCount', 'nav_button_count'],
        ['navKeyCount', 'nav_key_count'],
        ['navCardCount', 'nav_card_count'],
        ['ownAnswerCount', 'own_answer_count'],
        ['overlayCount', 'overlay_count'],
        ['filePageCount', 'file_page_count'],
        ['fileCount', 'file_count'],
        ['originalToggleCount', 'original_toggle_count'],
      ].forEach(([camel, snake]) => {
        eventData[snake] = clampChatUxNumber(meta[camel] ?? meta[snake], 10000);
      });
      break;
    case 'export':
      eventData.format = featureUxEnum(meta.format, SCORING_EXPORT_FORMATS);
      break;
    default:
      break;
  }
  return sendFeatureUxEvent('scoring_ux', 'scoring_ux', eventData);
};

/**
 * 평가계획서 페이지 단계 이벤트 → `assessment_ux_<action>`.
 * @returns {boolean} 화이트리스트에 없는 action이면 false
 */
export const trackAssessmentPlanUxEvent = (action, metadata = {}) => {
  if (!ASSESSMENT_UX_ACTION_SET.has(action)) return false;
  const meta = metadata && typeof metadata === 'object' ? metadata : {};
  const eventData = {
    ...buildFeatureUxBase(action, meta),
    plan_type: featureUxEnum(meta.planType ?? meta.plan_type, ASSESSMENT_PLAN_TYPES),
    analysis_mode: featureUxEnum(meta.analysisMode ?? meta.analysis_mode, ASSESSMENT_MODES),
  };
  switch (action) {
    case 'input_ready':
      eventData.source = featureUxEnum(meta.source, ASSESSMENT_SOURCES);
      break;
    case 'subjects_detected':
    case 'subject_choice':
      eventData.item_count = clampChatUxNumber(meta.itemCount ?? meta.item_count, 100);
      if (action === 'subject_choice') {
        eventData.choice = featureUxEnum(meta.choice, ASSESSMENT_CHOICES);
      }
      break;
    case 'generate_started':
    case 'generate_completed':
      eventData.has_hwp = meta.hasHwp === true || meta.has_hwp === true;
      eventData.source = featureUxEnum(meta.source, ASSESSMENT_SOURCES);
      break;
    case 'generate_failed':
      eventData.error_code = sanitizeChatUxToken(meta.errorCode ?? meta.error_code, 'unknown');
      break;
    case 'refine':
      eventData.scope = featureUxEnum(meta.scope, ASSESSMENT_REFINE_SCOPES);
      break;
    case 'save_completed':
      eventData.has_hwp = meta.hasHwp === true;
      break;
    case 'hwp_download':
      eventData.format = featureUxEnum(meta.format, ASSESSMENT_HWP_FORMATS);
      eventData.ok = meta.ok === true;
      break;
    case 'hwp_work_save':
      eventData.ok = meta.ok === true;
      eventData.reason = sanitizeChatUxToken(meta.reason, 'none');
      break;
    case 'hwp_skip_edits':
      eventData.target = featureUxEnum(meta.target, ASSESSMENT_HWP_SKIP_TARGETS);
      break;
    default:
      break;
  }
  return sendFeatureUxEvent('assessment_ux', 'assessment_ux', eventData);
};

const FILE_UPLOAD_STAGES = new Set(['uploaded', 'failed']);

/** 파일명에서 확장자만 뽑는다(1~5자 영숫자, 그 외 other). 파일명 자체는 절대 보내지 않는다. */
export const fileExtensionToken = (filename) => {
  const name = String(filename || '');
  const dot = name.lastIndexOf('.');
  const ext = dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
  return /^[a-z0-9]{1,5}$/.test(ext) ? ext : 'other';
};

/**
 * 파일 업로드
 *
 * extra(선택): stage(uploaded|failed), category, errorCode, extractionMethod, extractionStatus,
 * contentLength, pageCount, isTextbookLike — 토큰·숫자만 남긴다.
 */
export const trackFileUpload = (feature, fileType, fileSize, isPro = false, extra = {}) => {
  const eventData = {
    feature,
    file_type: fileType,
    file_size_kb: Math.round((Number(fileSize) || 0) / 1024),
    is_pro: isPro
  };
  if (FILE_UPLOAD_STAGES.has(extra.stage)) {
    eventData.stage = extra.stage;
    eventData.category = sanitizeChatUxToken(extra.category, 'general');
    eventData.error_code = sanitizeChatUxToken(extra.errorCode, 'none');
    eventData.extraction_method = sanitizeChatUxToken(extra.extractionMethod, 'none');
    eventData.extraction_status = sanitizeChatUxToken(extra.extractionStatus, 'none');
    eventData.content_length = clampChatUxNumber(extra.contentLength, 10_000_000);
    eventData.page_count = clampChatUxNumber(extra.pageCount, 10_000);
    eventData.is_textbook_like = extra.isTextbookLike === true;
  }
  // 여러 파일이 동시에 끝나도(동시 업로드 3개) 각각 한 건씩 남아야 한다.
  sendEvent('file_upload', eventData, { skipDedupe: true });
};

// ============================================================================
// 🟡 페이지뷰 및 세션 이벤트
// ============================================================================

/**
 * 페이지뷰
 */
export const trackPageView = (pagePath, pageTitle) => {
  sendEvent('page_view', {
    page_path: pagePath,
    page_title: pageTitle
  });
};

/**
 * 세션 시작
 */
export const trackSessionStart = () => {
  // UTM 초기화 (URL에서 추출 → localStorage 저장, 없으면 기존 값 유지)
  const currentUtm = initUtmParams();
  // 랜딩페이지 저장
  saveLandingPage();
  
  sendEvent('session_start', {
    landing_page: getLandingPage(),
    referrer: getReferrer(),
    utm: currentUtm,
    utm_source: currentUtm.utm_source || null,
    utm_medium: currentUtm.utm_medium || null,
    utm_campaign: currentUtm.utm_campaign || null,
    utm_content: currentUtm.utm_content || null
  });
};

/**
 * 세션 종료 (beforeunload에서 호출)
 */
export const trackSessionEnd = () => {
  try {
    const sessionDuration = Math.floor((Date.now() - getSessionStartTime()) / 1000);
    
    // user_id 결정 (sendEvent와 동일한 로직)
    let userId;
    const user = auth?.currentUser;
    if (user?.uid) {
      userId = user.uid;
    } else {
      try {
        userId = `anon_${getAnonymousUserId()}`;
      } catch {
        userId = 'anonymous';
      }
    }
    
    // Beacon API 사용 (페이지 이탈 시에도 전송 보장)
    const payload = {
      event_type: 'session_end',
      user_id: userId,
      session_id: getSessionId(),
      timestamp: new Date().toISOString(),
      event_data: {
        session_duration_seconds: sessionDuration
      }
    };
    
    const API_BASE_URL = getApiBaseUrl();
    navigator.sendBeacon(
      `${API_BASE_URL}/api/analytics/event`,
      JSON.stringify(payload)
    );
  } catch {
    // beforeunload에서는 에러 무시
  }
};

// ============================================================================
// 🟣 체험/구독 관련 이벤트
// ============================================================================

/**
 * 체험 기간 만료 예정 알림 노출
 */
export const trackTrialExpiryNotice = (remainingDays) => {
  sendEvent('trial_expiry_notice', {
    remaining_days: remainingDays
  });
};

/**
 * 구독 갱신
 */
export const trackSubscriptionRenewal = (plan, amount, renewalCount) => {
  sendEvent('subscription_renewal', {
    plan,
    amount,
    renewal_count: renewalCount
  }, { waitForResponse: true });
};

/**
 * 구독 만료
 */
export const trackSubscriptionExpired = (plan, totalDaysUsed) => {
  sendEvent('subscription_expired', {
    plan,
    total_days_used: totalDaysUsed
  });
};

// ============================================================================
// 🏠 홈페이지 CTA 이벤트
// ============================================================================

/**
 * 홈페이지 사전예약 클릭
 */
export const trackHomePreorderClick = (location = 'pricing_section') => {
  sendEvent('home_preorder_click', {
    location,
    event_name: '새학기 사전예약',
    funnel_step: 1
  });
};

/**
 * 홈페이지 첫 결제 할인 클릭
 * - promo가 'trial50'이면 체험 첫 결제 50% (TRIAL50 쿠폰)
 * - promo가 'newyear2026'이면 시즌 한정 최대 30% (NEWYEAR2026 쿠폰)
 */
export const trackHomeFirstPurchaseClick = (location = 'pricing_section', promo = 'trial50') => {
  const eventNameMap = {
    trial50: '체험 첫 결제 50% 할인',
    newyear2026: '시즌 한정 최대 30% 할인',
    renewal20: '연장 20% 할인',
  };
  sendEvent('home_first_purchase_click', {
    location,
    promo_code: promo,
    event_name: eventNameMap[promo] || '첫 결제 할인',
    funnel_step: 1
  });
};

/**
 * 홈페이지 무료체험 클릭
 */
export const trackHomeFreeTrialClick = (location = 'pricing_section') => {
  sendEvent('home_free_trial_click', {
    location,
    event_name: '7일 무료 체험',
    funnel_step: 1
  });
};

/**
 * 홈페이지 CTA 버튼 클릭 (써보기, 요금제 보기 등)
 */
export const trackHomeCtaClick = (ctaType, destination) => {
  sendEvent('home_cta_click', {
    cta_type: ctaType,  // 'try_now', 'view_pricing', 'learn_more'
    destination,
    funnel_step: 1
  });
};

// ============================================================================
// 초기화 및 유틸리티
// ============================================================================

// 마지막 추적된 페이지 (중복 방지)
let lastTrackedPath = '';

/**
 * 이벤트 트래커 초기화
 * App.js에서 한 번 호출
 */
export const initEventTracker = () => {
  // 세션 시작 추적
  trackSessionStart();
  
  // 페이지 이탈 시 세션 종료 추적
  window.addEventListener('beforeunload', trackSessionEnd);
  
  // 페이지뷰 중복 방지용 핸들러
  const handlePageView = () => {
    const currentPath = window.location.pathname;
    if (currentPath !== lastTrackedPath) {
      lastTrackedPath = currentPath;
      trackPageView(currentPath, document.title);
    }
  };
  
  // 히스토리 변경 감지 (SPA 라우팅)
  const originalPushState = window.history.pushState;
  window.history.pushState = function(...args) {
    originalPushState.apply(window.history, args);
    handlePageView();
  };
  
  const originalReplaceState = window.history.replaceState;
  window.history.replaceState = function(...args) {
    originalReplaceState.apply(window.history, args);
    handlePageView();
  };
  
  // 뒤로가기/앞으로가기 감지
  window.addEventListener('popstate', handlePageView);
  
  // 초기 페이지 설정
  lastTrackedPath = window.location.pathname;
};

// 기본 export
const eventTracker = {
  // 결제 퍼널
  trackProModalOpen,
  trackProModalClose,
  trackFreeTrialClick,
  trackFreeTrialStart,
  trackDiscountBubbleClick,
  trackPersonalOfferClick,
  trackPaymentPageView,
  trackPaymentStart,
  trackPaymentComplete,
  trackPaymentFailed,
  trackPaymentCancelled,
  trackPaymentAbandoned,
  
  // 회원가입 퍼널
  trackSignupPageView,
  trackSignupAttempt,
  trackSignupComplete,
  trackSignupCompleteOnce,
  resumePendingSignupComplete,
  trackFirstFeatureUse,
  
  // 🟣 로그인 모달 (비로그인 → 회원가입 퍼널)
  trackLoginModalOpen,
  trackLoginModalClose,
  trackLoginModalSignupClick,
  
  // 기능 사용
  trackFeatureUse,
  trackFeatureComplete,
  trackEvent,
  trackChatUxEvent,
  trackSaenggibuUxEvent,
  trackScoringUxEvent,
  trackAssessmentPlanUxEvent,
  trackWorksheetEvent,
  trackPptEvent,
  trackClassroomBundleEvent,
  trackClassActivityEvent,
  trackClassPlayEvent,
  trackInAppBrowserEvent,
  trackAi30Event,
  trackFestaEvent,
  trackGroup50Event,
  trackProAiEvent,
  trackScoringLpEvent,
  trackGroup50CheckoutView,
  trackAi30TrialAppliedPixel,
  trackHwpDocEvent,
  ensureDefaultCampaignUtm,
  trackFileUpload,
  
  // 페이지뷰/세션
  trackPageView,
  trackSessionStart,
  trackSessionEnd,
  
  // 체험/구독
  trackTrialExpiryNotice,
  trackSubscriptionRenewal,
  trackSubscriptionExpired,
  
  // 초기화
  initEventTracker
};

export default eventTracker;

// ============================================================================
// 학급 관리(우리 반 명단) 객관 행동 로그. 학생 이름·명단 이름·파일명은 보내지 않고 숫자와 정해진 값만 남긴다.
// ============================================================================

export const CLASS_ROSTER_ACTIONS = Object.freeze([
  'class_roster_page_viewed',
  'class_roster_create_opened',
  'class_roster_create_cancelled',
  'class_roster_file_read',
  'class_roster_saved',
  'class_roster_save_prompt_shown',
  'class_roster_edited',
  'class_roster_copied',
  'class_roster_deleted',
  'class_roster_sheet_downloaded',
  'class_roster_start',
  'class_roster_matched',
  'class_roster_name_fixed',
  'class_roster_name_kept',
  'class_roster_order_fill_preview',
  'class_roster_order_filled',
  'class_roster_order_fill_undone',
  'room_roster_linked',
  'room_roster_unlinked',
  'student_records_viewed',
  'student_record_opened',
  'student_saenggibu_start',
  'class_overlap_check',       // 학생별 기록 '반 전체 점검' (영역 수·고칠 학생 수·심각도)
  'student_memo_added',        // 학생 메모 남기기 (개수·글자 수·영역 골랐는지·쓸 수 없는 내용인지)
  'student_memo_edited',
  'student_memo_deleted',
  'student_profile_saved',     // 임원·진로희망 저장 (입력했는지만)
  'student_single_start',      // 학생 화면에서 '이 학생 생기부 쓰기' (넣은 메모 수)
  'student_memo_from_form',    // 단일 생성에서 '입력한 관찰을 메모로 남기기'
  'class_roster_result_shown', // 결과 화면 '학생별로 모아두기' 카드 (kind=prompt|linked)
  'class_roster_result_dismissed',
  'class_roster_records_opened', // 결과 화면 카드에서 '학생별 기록 보기'
  'class_memo_sheet_opened',   // 반 화면 학생 칸을 눌러 메모 시트 열기 (scope=one|group)
  'class_memo_quick_saved',    // 메모 시트에서 남기기 (mode=tag|text|tag_text, 학생 수, 오늘이 아닌 날짜인지)
  'class_memo_paste_saved',    // 붙여넣기 나누기로 남기기 (줄 수·맞춘 수·못 맞춘 수)
  'class_memo_prompt_clicked', // 채점 결과 화면 '이 반 메모 남기기'
]);
const CLASS_ROSTER_ACTION_SET = new Set(CLASS_ROSTER_ACTIONS);

const CLASS_ROSTER_ENUMS = Object.freeze({
  source: ['classes', 'batch', 'scoring', 'auto', 'preferred', 'switch', 'saved', 'batch_result', 'scoring_result'],
  surface: ['batch', 'scoring', 'classes', 'room'],
  created_from: ['paste', 'file', 'batch', 'scoring'],
  target: ['scoring', 'saenggibu'],
  file_ext: ['xlsx', 'xls', 'csv', 'txt', 'hwp', 'hwpx', 'docx', 'pdf', 'other'],
  outcome: ['ok', 'no_names', 'rejected', 'error'],
  how: ['similar', 'none', 'name', 'alias', 'file_name'],
  when: ['current', 'next', 'past'],
  scope: ['one', 'class', 'group'],
  mode: ['tag', 'text', 'tag_text'],
  category: ['행동발달', '교과', '창체', '동아리', '진로'],
  level: ['none', 'overlap', 'audit', 'national'],
  kind: ['prompt', 'linked', 'verified', 'guessed'],
  via: ['select', 'chip', 'recommended', 'clue'],
});

const CLASS_ROSTER_COUNTS = Object.freeze({
  student_count: 300,
  names_count: 300,
  total: 300,
  confirmed: 300,
  exact_count: 300,
  alias_count: 300,
  similar_count: 300,
  unmatched_count: 300,
  file_name_count: 300,
  clue_count: 300,
  count: 300,
  anchor_count: 300,
  roster_count: 100,
  current_count: 100,
  next_count: 100,
  past_count: 100,
  added: 300,
  removed: 300,
  renamed: 300,
  aliases_removed: 300,
  semester: 2,
  to_semester: 2,
  year_offset: 1,
  age_days: 3650,
  elapsed_ms: 600_000,
  size_kb: 100_000,
  submitted_count: 500,
  missing_count: 500,
  overlap_pct: 100,
  linked_count: 300,
  record_count: 5000,
  group_count: 50,
  flagged_count: 300,
  same_count: 1000,
  near_count: 1000,
  max_people: 300,
  memo_count: 500,
  memo_chars: 2000,
  line_count: 1000,
  scoring_count: 300,
  other_year_count: 500,
});

const CLASS_ROSTER_FLAGS = [
  'reordered', 'label_changed', 'semester_changed', 'has_saved_rosters',
  'has_area', 'has_restricted', 'has_role', 'has_career', 'not_today',
];
const CLASS_ROSTER_ID_PATTERN = /^[A-Za-z0-9_-]{6,64}$/;

export const trackClassRosterEvent = (action, metadata = {}) => {
  if (!CLASS_ROSTER_ACTION_SET.has(action)) return false;
  const meta = metadata && typeof metadata === 'object' ? metadata : {};
  const eventData = {};
  Object.entries(CLASS_ROSTER_ENUMS).forEach(([key, allowed]) => {
    if (meta[key] === undefined || meta[key] === null || meta[key] === '') return;
    eventData[key] = allowed.includes(meta[key]) ? meta[key] : 'unknown';
  });
  Object.entries(CLASS_ROSTER_COUNTS).forEach(([key, maximum]) => {
    if (meta[key] === undefined || meta[key] === null || meta[key] === '') return;
    if (!Number.isFinite(Number(meta[key]))) return;
    eventData[key] = clampChatUxNumber(meta[key], maximum);
  });
  CLASS_ROSTER_FLAGS.forEach((key) => {
    if (typeof meta[key] === 'boolean') eventData[key] = meta[key];
  });
  const rosterId = String(meta.roster_id || '').trim();
  if (CLASS_ROSTER_ID_PATTERN.test(rosterId)) eventData.roster_id = rosterId;

  try {
    window.gtag?.('event', action, { ...eventData, event_category: 'class_roster' });
  } catch {
    // GA 실패는 명단 흐름을 막지 않는다.
  }
  sendEvent(action, eventData);
  return true;
};
